/* eslint-disable react/sort-comp, operator-assignment, no-restricted-syntax, */
/// <reference path="./timeline-types.js" />

import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { fromJS } from "immutable";
import MouseDrag from "./helper-components/timeline-mousedrag";
import AutoScroll from "./helper-components/timeline-autoscroll";
import timelineTileReducer, { TIMELINE_TILE_ACTIONS, getTimelineTileInitialState } from "./timeline-tile-reducer";
import TimelineScrollProvider from "./contexts/timeline-scroll-context";
import { TimelineContainer, TimelineResize } from "./timeline-components";
import { addObject, setCurrentState, setTimelineHeight, updateProjectData, updateTimelineTime } from "../../redux/actions/timelineUtils";
import { setPlayHeadTime, setPropertyPanel, setPropertyWindow, setReframeMode, setSelectedItems, setSelectedZoomId } from "../../redux/actions/appUtils";
import { PLAYER_CONTROLS_HEIGHT, RULER_OPTIONS, SLIDER_GAP_TYPES, SLIDER_TYPES, TIMELINE_MODES, TRACK_TYPES, transitionTime } from "./timeline-constants";
import { addDefaultSnapPoints, addGapSlider, adjustTimeScale, calculateStepperSize, calculateTimelinePlot, getGapSliderIds, getMagnetSliderIds, getMouseClientPosition, getScrollOptions, getSliders, getTrackPosition, getTracks, getUpdatedTimeline, isImageOnly, isReframeDropItem, isUpImageSVG, isVideoOnly, iterViewportSliders, memoize, movePlayheadToNearestThumb, moveSliders, moveToTrack, randomString, removeDefaultSnapPoints, removeSliderGaps, secondsToStep, shouldExtendRuler, splitSliderHelper, splitSubtitle, updateTrackWidth } from "./timeline-helper";
import PlayerControls from "./playercontrols/timeline-playercontrols";
import TimelineInner from "./timeline-inner/timeline-inner";
import Ruler from "./ruler/timeline-ruler";
import TrackList from "./tracks/timeline-tracks";
import Playhead from "./playhead/timeline-playhead";
import WaveformManager from "./timeline-waveformmanager";
import { PANEL, PROJECT_CONTAINERS } from "../../constants";
import { addSubtitleTimeline } from "../../helper/subtitleHelper";
import { showFocusSpaceErrorModal, showUpgrade } from "../../redux/actions/modalAction";
import content from "../../constants/content";
import { applyZoomToTimeline } from "../../helper/smartfocusHelper";

const indicatorId = SLIDER_TYPES.PLAYHEAD_INDICATOR;
const playheadId = SLIDER_TYPES.PLAYHEAD_THUMB;
const rulerId = SLIDER_TYPES.RULER;
const tracksOffsetY = PLAYER_CONTROLS_HEIGHT + RULER_OPTIONS.height;

const getParentItemIdFromSubtitleId = (project, timelineId) => {
    if (!timelineId) return null;
    const subtitleItem = project.getIn(["localSubtitle", timelineId])
        || project.getIn(["localZoom", timelineId])
        || project.getIn(["localAiReframe", timelineId]);
    if (subtitleItem) {
        return subtitleItem.getIn(["subtitleId", "itemId"]) || subtitleItem.getIn(["smartFocusId", "itemId"]);
    }
    if (timelineId.includes("-newreframe")) {
        const parts = timelineId.split("-newreframe");
        if (parts.length > 1) {
            return parts[1].split("-")[0];
        }
    }
    return timelineId.split("-")[0];
};

const findWorkspaceItemByAnyId = (workspaceItems, itemId) => {
    if (!workspaceItems || !itemId) return null;

    const directItem = workspaceItems.get(itemId);
    if (directItem) return directItem;

    return workspaceItems.find((item) => (
        item?.get("id") === itemId
        || item?.get("dropId") === itemId
    ));
};

const getReframeParentItemId = ({
    project,
    selectedItems,
    selectedSubtitles,
    playhead,
}) => {
    const workspaceItems = project.get("workspaceItems");
    const localAiReframe = project.get("localAiReframe");

    if (selectedSubtitles?.size > 0) {
        const selectedSubtitleId = selectedSubtitles.get(0);
        const reframeItem = project.getIn(["localAiReframe", selectedSubtitleId]);
        const reframeParentId = reframeItem?.getIn(["smartFocusId", "itemId"]);
        if (reframeParentId) return reframeParentId;

        const sceneItem = findWorkspaceItemByAnyId(workspaceItems, selectedSubtitleId);
        if (sceneItem?.get("sceneData")) {
            return sceneItem.get("originalId") || sceneItem.get("dropId") || sceneItem.get("id");
        }
        if (sceneItem) {
            return sceneItem.get("id") || selectedSubtitleId;
        }
    }

    if (selectedItems?.size > 0) {
        const selectedItemId = selectedItems.get(0);
        const selectedItem = findWorkspaceItemByAnyId(workspaceItems, selectedItemId);
        if (selectedItem?.get("sceneData")) {
            return selectedItem.get("originalId") || selectedItem.get("dropId") || selectedItem.get("id");
        }
        if (selectedItem) {
            return selectedItem.get("id") || selectedItemId;
        }
    }

    if (localAiReframe?.size) {
        const playheadReframeItem = localAiReframe.find((reframeItem) => (
            playhead >= (reframeItem.get("enterStart") || 0) - 0.001
            && playhead <= (reframeItem.get("exitEnd") || 0) + 0.001
        ));
        const playheadParentId = playheadReframeItem?.getIn(["smartFocusId", "itemId"]);
        if (playheadParentId) return playheadParentId;

        return localAiReframe.first()?.getIn(["smartFocusId", "itemId"]);
    }

    return null;
};

const getReframeSliderIdAtPlayhead = ({ sliders, stepper, playhead }) => {
    if (!sliders || !stepper) return null;

    let matchedSliderId = null;
    let bestEnterStep = -Infinity;

    for (const slider of Object.values(sliders)) {
        if (
            slider.sliderType === SLIDER_TYPES.REFRAME
            && slider.enterStart?.position?.step !== undefined
            && slider.exitEnd?.position?.step !== undefined
        ) {
            const enterStep = slider.enterStart.position.step;
            const exitStep = slider.exitEnd.position.step;
            const enterTime = enterStep * stepper.interval;
            const exitTime = exitStep * stepper.interval;
            if (
                playhead >= enterTime - 0.001
                && playhead <= exitTime + 0.001
                && enterStep >= bestEnterStep
            ) {
                bestEnterStep = enterStep;
                matchedSliderId = slider.id;
            }
        }
    }

    return matchedSliderId;
};

class TimelineComponent extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            timelineTile: getTimelineTileInitialState({
                playhead: props.playhead,
                project: props.project,
                sideBarMenuWidth: props.sideBarMenuWidth,
                timelineMode: props.timelineMode,
                interval: props.interval
            }),
            timelinePlotBeforeDrag: null,
            animatePlayhead: false,
            selectedSliders: [],
            timelineAutoScrollData: null,
            isSliderMultiSelect: false,
            sliderMoved: false,
            animateTimelineContainer: false,
            isShowPlayControls: true
        }

        this.dispatchTimelineTile = this.dispatchTimelineTile.bind(this);
        this.setSelectedSliders = this.setSelectedSliders.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.resizeTimeline = this.resizeTimeline.bind(this);
        this.stopTimelineResize = this.stopTimelineResize.bind(this);
        this.handleResizeMouseDown = this.handleResizeMouseDown.bind(this);
        this.changeTimeScale = this.changeTimeScale.bind(this);
        this.onTimelineAutoScroll = this.onTimelineAutoScroll.bind(this);
        this.handleSlidersDrag = this.handleSlidersDrag.bind(this);
        this.handleSlidersDragEnd = this.handleSlidersDragEnd.bind(this);
        this.movePlayheadIndication = this.movePlayheadIndication.bind(this);
        this.handleTimelineMouseMove = this.handleTimelineMouseMove.bind(this);
        this.hidePlayheadIndication = this.hidePlayheadIndication.bind(this);
        this.handleTimelineMouseLeave = this.handleTimelineMouseLeave.bind(this);
        this.selectSlider = this.selectSlider.bind(this);
        this.removeGapSlider = this.removeGapSlider.bind(this);
        this.placePlayheadAtTime = this.placePlayheadAtTime.bind(this);
        this.placePlayheadOnClick = this.placePlayheadOnClick.bind(this);
        this.adjustPlayheadStepBy = this.adjustPlayheadStepBy.bind(this);
        this.updateIsSliderMultiSelect = this.updateIsSliderMultiSelect.bind(this);
        this.handleGapRemove = this.handleGapRemove.bind(this);
        this.autoScrollPlayhead = this.autoScrollPlayhead.bind(this);
        this.scrollToFirstSelection = this.scrollToFirstSelection.bind(this);
        this.updateStorePlayhead = this.updateStorePlayhead.bind(this);
        this.onSeekClick = this.onSeekClick.bind(this);
        this.onContainerTransitionEnd = this.onContainerTransitionEnd.bind(this);
        this.rulerScrollBy = this.rulerScrollBy.bind(this);
        this.splitHandler = this.splitHandler.bind(this);
        this.toggleSubtitleMode = this.toggleSubtitleMode.bind(this);
        this.toggleAiReframeMode = this.toggleAiReframeMode.bind(this);
        this.toggleSmartFocusMode = this.toggleSmartFocusMode.bind(this);
        this.addSubtitle = this.addSubtitle.bind(this);
        this.isOverlapping = this.isOverlapping.bind(this);
        this.addZoom = this.addZoom.bind(this);
        this.zoomClear = this.zoomClear.bind(this);

        /** @type {React.MutableRefObject<HTMLElement | null>} */
        this.timelineInnerRef = React.createRef(null);
        /** @type {React.MutableRefObject<HTMLElement | null>} */
        this.trackListContainerRef = React.createRef(null);
        // Ref to store pending AIReframe scene selection to be applied after mode change
        this.pendingReframeSelection = null;
        // Ref to store all SPLIT/THREE/FOUR subtitle IDs that need the black border highlight.
        this.pendingReframeHighlight = null;

        this.dragStartThreshold = 10;
        this.sliderMoved = false;
        this.isDraggingActive = false;
        this.isSliderMultiSelect = false;
        this.preventSliderDeselect = false;
        this.playSeekTimer = null;

        this.checkGapStatus = memoize(({ selectedSliders, sliders }) => {
            return {
                canUseMagnet: getMagnetSliderIds({ selectedSliders, sliders }).canUseMagnet,
                canDeleteGap: getGapSliderIds({ deleteType: "gap-delete-selected", selectedSliders, sliders }).canDeleteGap,
            };
        });
    }

    componentDidMount() {
        this.props.updateProjectData({ timelinePlaceholder: null });
        this.props.setTimelineHeight(this.state.timelineTile.timelinePlot);
        // To place the workspace in fit position for Mobile responsive.
        if (this.props.isMobileView) {
            setTimeout(() => {
                this.stopTimelineResize({ clickType: "expand" });
            }, 500);
        }
        window.addEventListener("resize", this.handleWindowResize);
    }

    componentDidUpdate(prevProps) {
        const { timelineTile, } = this.state;
        const { sliders, stepper, timelinePlot } = timelineTile;
        let updatedSliders = sliders;

        if (
            this.props.project !== prevProps.project
            || this.props.timelineMode !== prevProps.timelineMode
            || this.props.timelineResetToken !== prevProps.timelineResetToken
        ) {

            if (this.isDraggingActive) {
                return;
            }
            const { project, timelineMode, } = this.props;

            let playheadTime = 0;
            if (sliders && sliders[playheadId] && sliders[playheadId].enterStart) {
                const playheadThumb = sliders[playheadId].enterStart;
                playheadTime = playheadThumb.position.step * stepper.interval;
                if (playheadTime > project.get("duration")) {
                    playheadTime = project.get("duration");
                }
            }
            if (this.props.playhead !== prevProps.playhead) {
                playheadTime = this.props.playhead
            } else if (this.props.selectedSubtitles !== prevProps.selectedSubtitles && this.props.selectedSubtitles.size) {
                // selectedSubtitles and project will change when subtitles are added/deleted
                // if project is changed and some subtitle is selected, then it might be added one
                // so move playhead to first selected subtitle
                const subtitleItem = project.getIn(["localSubtitle", this.props.selectedSubtitles.first()]);
                if (subtitleItem) {
                    // check subtitle-item.jsx file for reason for ceil
                    playheadTime = Math.ceil((subtitleItem.get("enterStart") / stepper.interval)) * stepper.interval;
                }
            }
            const newStepper = adjustTimeScale({ newProject: project, stepper, timelinePlot, interval: this.props.interval });
            const selectedSubtitleData = this.props.selectedSubtitles;
            let currentSelectedItem = selectedSubtitleData.size > 0
                ? getParentItemIdFromSubtitleId(project, selectedSubtitleData.get(0))
                : this.props.selectedItems.get(0);
            if (this.props.selectedItems.size > 0) {
                const wItems = this.props.project.getIn(["workspaceItems"]);
                const item = wItems.find(
                    (workspaceItem) => workspaceItem.get("id") === currentSelectedItem
                );
                currentSelectedItem = item?.get("dropId");
            }
            // split("-")[0] truncates it incorrectly. Look up the item by full ID to get its dropId.
            if (timelineMode === TIMELINE_MODES.REFRAME && selectedSubtitleData.size > 0) {
                currentSelectedItem = getReframeParentItemId({
                    project,
                    selectedItems: this.props.selectedItems,
                    selectedSubtitles: selectedSubtitleData,
                    playhead: playheadTime,
                });
            } else if (timelineMode === TIMELINE_MODES.REFRAME) {
                currentSelectedItem = getReframeParentItemId({
                    project,
                    selectedItems: this.props.selectedItems,
                    selectedSubtitles: selectedSubtitleData,
                    playhead: playheadTime,
                });
            }
            // Below code needs to ensure for split approach

            // const wItems = this.props.project.getIn(["workspaceItems"]);
            // let currentSelectedItem = selectedSubtitleData.size > 0 ? selectedSubtitleData?.get(0) : this.props.selectedItems.get(0);
            // // Try direct workspace key lookup first (new format: timelineId = itemId)
            // const sceneItemByKey = currentSelectedItem ? wItems.get(currentSelectedItem) : null;
            // if (sceneItemByKey?.get("sceneData")) {
            //     currentSelectedItem = sceneItemByKey.get("originalId");
            // } else {
            //     // Fallback: sceneId-based lookup for backward compatibility
            //     const sceneIdPart = selectedSubtitleData?.get(0)?.split("-")[0];
            //     // eslint-disable-next-line consistent-return
            //     wItems.forEach((workspaceItem) => {
            //         if (workspaceItem.get("sceneId") === sceneIdPart) {

            const newTracks = getTracks({
                project, stepper: newStepper, timelineMode, currentSelectedItem,
            });
            const { sliders: newSliders, timelineSnapPoints: newTimelineSnapPoints } = getSliders({
                playhead: playheadTime,
                project,
                stepper: newStepper,
                tracks: newTracks,
                timelineMode,
            });
            updatedSliders = newSliders;

            const hasSelectedReframe = this.props.selectedSubtitles.some(id => {
                const slider = newSliders[id];
                return slider && slider.sliderType === SLIDER_TYPES.REFRAME;
            });

            let matchedSliderId = null;
            // Auto-select scene at playhead when entering or rebuilding REFRAME mode.
            if (
                timelineMode === TIMELINE_MODES.REFRAME
                && !this.pendingReframeSelection
            ) {
                const playheadMatchedSliderId = getReframeSliderIdAtPlayhead({
                    sliders: newSliders,
                    stepper: newStepper,
                    playhead: playheadTime,
                });
                const currentSelectedReframeId = this.props.selectedSubtitles.find(id => {
                    const slider = newSliders[id];
                    return slider && slider.sliderType === SLIDER_TYPES.REFRAME;
                });
                let shouldSelectMatched = false;
                if (playheadMatchedSliderId) {
                    if (!hasSelectedReframe) {
                        shouldSelectMatched = true;
                    } else if (currentSelectedReframeId !== playheadMatchedSliderId) {
                        const currentReframe = this.props.project.getIn(["localAiReframe", currentSelectedReframeId]);
                        const matchedReframe = this.props.project.getIn(["localAiReframe", playheadMatchedSliderId]);
                        const isSameTimeframe = currentReframe && matchedReframe &&
                            currentReframe.get("enterStart") === matchedReframe.get("enterStart") &&
                            currentReframe.get("exitEnd") === matchedReframe.get("exitEnd");
                        if (!isSameTimeframe) {
                            shouldSelectMatched = true;
                        }
                    }
                }
                if (shouldSelectMatched) {
                    matchedSliderId = playheadMatchedSliderId;
                }
            }

            this.dispatchTimelineTile({
                type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
                payload: {
                    stepper: newStepper,
                    tracks: newTracks,
                    sliders: newSliders,
                    timelineSnapPoints: newTimelineSnapPoints,
                    matchingSnapPoints: {},
                },
            }, () => {
                if (matchedSliderId) {
                    this.setSelectedSliders({
                        selectedSliders: [{ sliderId: matchedSliderId }]
                    });
                }
                if (
                    this.pendingReframeSelection &&
                    this.props.timelineMode === TIMELINE_MODES.REFRAME &&
                    prevProps.timelineMode !== TIMELINE_MODES.REFRAME
                ) {
                    const propData = this.pendingReframeSelection;
                    this.pendingReframeSelection = null;
                    const allSubtitles = propData.selectedSubtitles || [];
                    const firstSubtitle = allSubtitles[0];
                    if (allSubtitles.length > 1) {
                        this.pendingReframeHighlight = allSubtitles;
                    }
                    if (firstSubtitle) {
                        this.setSelectedSliders({
                            selectedSliders: [{ sliderId: firstSubtitle }]
                        });
                    } else {
                        // Clear selection if empty
                        this.setSelectedSliders({ selectedSliders: [] });
                    }
                }
            });
        } else if (this.props.playheadResetToken !== prevProps.playheadResetToken) {
            this.placePlayheadAtTime(this.props.playhead);
        }

        // Auto-select scene when playhead changes while already in REFRAME mode
        if (
            this.props.timelineMode === TIMELINE_MODES.REFRAME
            && prevProps.timelineMode === TIMELINE_MODES.REFRAME
            && this.props.playhead !== prevProps.playhead
            && !this.isDraggingActive
        ) {
            const { sliders, stepper } = this.state.timelineTile;
            const currentSelectedId = this.props.selectedSubtitles.get(0);
            const matchedSliderId = getReframeSliderIdAtPlayhead({
                sliders,
                stepper,
                playhead: this.props.playhead,
            });

            if (matchedSliderId && currentSelectedId !== matchedSliderId) {
                let shouldSelectMatched = true;
                if (currentSelectedId) {
                    const currentReframe = this.props.project.getIn(["localAiReframe", currentSelectedId]);
                    const matchedReframe = this.props.project.getIn(["localAiReframe", matchedSliderId]);
                    const isSameTimeframe = currentReframe && matchedReframe &&
                        currentReframe.get("enterStart") === matchedReframe.get("enterStart") &&
                        currentReframe.get("exitEnd") === matchedReframe.get("exitEnd");
                    if (isSameTimeframe) {
                        shouldSelectMatched = false;
                    }
                }
                if (shouldSelectMatched) {
                    const isAlreadySelected = this.state.selectedSliders.some(s => s.sliderId === matchedSliderId);
                    if (!isAlreadySelected) {
                        this.setSelectedSliders({
                            selectedSliders: [{ sliderId: matchedSliderId }]
                        });
                    }
                }
            }
        }

        if (
            this.props.isPlayAll !== prevProps.isPlayAll
            || this.props.t1 !== prevProps.t1
            || this.props.isPlayerLoaded !== prevProps.isPlayerLoaded
        ) {
            clearInterval(this.playSeekTimer);
            const { isPlayAll, t1, isPlayerLoaded, playhead } = this.props;

            if (isPlayAll && t1 && isPlayerLoaded) {
                this.playSeekTimer = setInterval(() => {
                    // use recent props for interval callback
                    if (this.props.isPlayAll && this.props.isPlayerLoaded) {
                        const curTime = t1.time();
                        this.dispatchTimelineTile({
                            type: TIMELINE_TILE_ACTIONS.SEEK_PLAYHEAD_CHANGED,
                            payload: { time: curTime },
                        }, this.autoScrollPlayhead);
                    }
                }, 1000 / 30);
            } else if (!isPlayAll) {
                this.dispatchTimelineTile({
                    type: TIMELINE_TILE_ACTIONS.SEEK_PLAYHEAD_CHANGED,
                    payload: { isReset: true, playhead },
                });
            }
        }

        if (
            this.props.selectedItems !== prevProps.selectedItems
            || this.props.selectedAudios !== prevProps.selectedAudios
            || this.props.selectedSubtitles !== prevProps.selectedSubtitles
        ) {
            const { selectedSliders } = this.state;
            const newSelectedSliders = selectedSliders.filter(s => {
                // to retain both playhead and gap selection
                const slider = updatedSliders[s.sliderId];
                return (
                    slider
                    && (
                        slider.sliderType === SLIDER_TYPES.GAP
                        || slider.sliderType === SLIDER_TYPES.PLAYHEAD_THUMB
                    )
                );
            });
            const storeSelectionCount = (
                this.props.selectedItems.size
                + this.props.selectedAudios.size
                + this.props.selectedSubtitles.size
            );
            let matchedWithInternal = 0;
            const selectionToHandle = [this.props.selectedItems, this.props.selectedAudios, this.props.selectedSubtitles];

            for (const selectionList of selectionToHandle) {
                for (const sliderId of selectionList.valueSeq()) {
                    const internalHasSelection = selectedSliders.some((selection) => selection.sliderId === sliderId);
                    if (internalHasSelection) {
                        matchedWithInternal = matchedWithInternal + 1;
                    }
                    newSelectedSliders.push({ sliderId });
                }
            }

            // For SPLIT/THREE/FOUR AI Reframe layouts the Redux store intentionally only
            if (
                this.props.timelineMode === TIMELINE_MODES.REFRAME
                && this.props.selectedSubtitles !== prevProps.selectedSubtitles
                && this.props.selectedSubtitles.size > 0
            ) {
                // Ensure only the selected individual scene is highlighted,
                // and restrict any auto-expansion to the same parent clip if needed.
                // However, for "individual scene item" requirement, we avoid expanding to all sibling items of the same sceneId.
                const clickedId = this.props.selectedSubtitles.get(0);
                if (!newSelectedSliders.some(s => s.sliderId === clickedId)) {
                    newSelectedSliders.push({ sliderId: clickedId });
                }
            }

            if (this.pendingReframeHighlight) {
                for (const id of this.pendingReframeHighlight) {
                    if (!newSelectedSliders.some(s => s.sliderId === id)) {
                        newSelectedSliders.push({ sliderId: id });
                    }
                }
                this.pendingReframeHighlight = null;
            }

            if (newSelectedSliders.length !== selectedSliders.length || matchedWithInternal !== storeSelectionCount) {
                let onSelectionUpdate;

                if (this.props.timelineSelectionAutoScrollToken !== prevProps.timelineSelectionAutoScrollToken) {
                    onSelectionUpdate = this.scrollToFirstSelection;
                }

                this.setSelectedSliders({
                    selectedSliders: newSelectedSliders,
                    updateStore: false,
                    setStateCallback: onSelectionUpdate,
                });
            }
        }

        if (this.props.isEnableVersionHistory !== prevProps.isEnableVersionHistory) {
            this.stopTimelineResize({
                clickType: this.props.isEnableVersionHistory ? "minimize" : "expand"
            })
        }



        if (
            (this.props.isEnableVersionHistory !== prevProps.isEnableVersionHistory && this.props.isEnableVersionHistory) ||
            (this.state.isShowPlayControls &&
                ((this.props.swapDetails !== prevProps.swapDetails && this.props.swapDetails !== null) || this.props.isCropping))
        ) {
            this.setState({ isShowPlayControls: false });
        } else if (
            !this.props.isEnableVersionHistory &&
            this.props.swapDetails === null &&
            !this.state.isShowPlayControls &&
            !this.props.isCropping
        ) {
            this.setState({ isShowPlayControls: true });
        }

    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleWindowResize);
        clearInterval(this.playSeekTimer);
        this.checkGapStatus.clear();
    }

    /**
     * Function to keep playhead state in redux in-sync with internal state
     * Always use this function to update internal state of timeline
     * @param {TimelineTileProxyAction} action
     * @param {Function | undefined} setStateCallback
     */
    dispatchTimelineTile(action, setStateCallback) {
        const { updateStorePlayhead = true } = action;
        const { timelineTile } = this.state;

        let updatedSliders = null;
        if (action.type === TIMELINE_TILE_ACTIONS.SET_SLIDERS) {
            updatedSliders = action.payload;
        } else if (action.type === TIMELINE_TILE_ACTIONS.MERGE_STATE) {
            updatedSliders = action.payload && action.payload.sliders;
        }

        const newTimelineTile = timelineTileReducer(timelineTile, action);
        if (timelineTile !== newTimelineTile || setStateCallback) {
            this.setState({ timelineTile: newTimelineTile }, setStateCallback);
        }

        if (updatedSliders && updateStorePlayhead) {
            this.updateStorePlayhead(updatedSliders, newTimelineTile.stepper);
        }
    }

    /**
     * Function to keep selectedItems and selectedAudios always in-sync with internal state
     * Always use this function to update internal state of selectedSliders
     * @param {object} params
     * @param {SelectedSliders} params.selectedSliders
     * @param {Function | undefined} params.setStateCallback
     * @param {boolean | undefined} params.updateStore if false, function will skip updating redux
     */
    setSelectedSliders({ selectedSliders = [], setStateCallback, updateStore = true } = {}) {
        const { timelineTile } = this.state;
        const { sliders } = timelineTile;

        if (!selectedSliders) {
            selectedSliders = [];
        }
        if (updateStore) {
            const selectedItems = [];
            const selectedAudios = [];
            let selectedSubtitles = [];
            let checkSliderType = "";

            for (const selection of selectedSliders) {
                if (sliders[selection.sliderId]) {
                    const { sliderType, id } = sliders[selection.sliderId];
                    if (sliderType === SLIDER_TYPES.OBJECT) {
                        selectedItems.push(id);
                    } else if (sliderType === SLIDER_TYPES.AUDIO) {
                        selectedAudios.push(id);
                    } else if (sliderType === SLIDER_TYPES.SUBTITLE || sliderType === SLIDER_TYPES.SMART_FOCUS ||
                        sliderType === SLIDER_TYPES.REFRAME) {
                        selectedSubtitles.push(id);
                        checkSliderType = sliderType
                    }
                }
            }

            if (selectedItems.length || selectedAudios.length) {
                selectedSubtitles = [];
            }

            const selection = {
                selectedItems,
                selectedAudios,
                selectedSubtitles,
            };

            if (selectedItems.length === 1 && selectedAudios.length === 0) {
                const selectedItem = this.props.project.getIn(["workspaceItems", selectedItems[0]]);

                if (selectedItem && !selectedItem.get("isLock")) {
                    selection.type = selectedItem.get("type");
                    selection.subType = selectedItem.get("subType");

                    if (selectedItem.get("isSingleClipFrame")) {
                        // this frame does not have settings panel yet
                    } else if (selectedItem.get("type") === "TEXT") {
                        selection.propWindowType = "TEXT";
                    } else if (isImageOnly(selectedItem.get("type"), selectedItem.get("subType")) && !isUpImageSVG(selectedItem)) {
                        selection.propWindowType = "IMAGE_SETTINGS";
                    } else if (isVideoOnly(selectedItem.get("type"), selectedItem.get("subType"))) {
                        selection.propWindowType = "VIDEO_SETTINGS";
                    }
                }
            } else if (selectedItems.length === 0 && selectedAudios.length === 1) {
                const selectedAudio = this.props.project.getIn(["audios", selectedAudios[0]]);

                if (selectedAudio) {
                    selection.propWindowType = "AUDIO_SETTINGS";
                }
            } else if (checkSliderType === SLIDER_TYPES.SMART_FOCUS) {
                this.props.setSelectedZoomId(selection?.selectedSubtitles[0]);
                selection.propWindowType = "SMART_FOCUS";
            }
            if (checkSliderType) {
                selection.type = checkSliderType;
            }

            this.props.setSelectedItems(selection);
        }
        this.setState({ selectedSliders }, setStateCallback);
    }

    /**
     * @param {object} params
     * @param {"trackPosition" | "position"} params.positionToUse
     * @param {"play" | "nearest-thumb"} params.autoscrollType
     */
    autoScrollPlayhead(params = {}) {
        const { positionToUse = "trackPosition", autoscrollType = "play" } = params;
        const timelineInnerEl = this.timelineInnerRef.current;
        if (!timelineInnerEl) {
            return;
        }

        const { timelineTile } = this.state;
        const { sliders } = timelineTile;
        const playheadSlider = sliders[playheadId];
        const position = playheadSlider.enterStart[positionToUse];

        if (!position || (autoscrollType === "play" && !playheadSlider.isSeeking)) {
            return;
        }

        const { scrollLeft, offsetWidth } = timelineInnerEl;
        const playheadX = position.x;
        if (scrollLeft + offsetWidth < playheadX || playheadX < scrollLeft) {
            let offsetMultiplier = 1;
            if (autoscrollType === "nearest-thumb") {
                offsetMultiplier = 0.5;
            }
            const moveBy = playheadX - (scrollLeft + offsetWidth) + offsetWidth * offsetMultiplier;
            timelineInnerEl.scrollBy({ left: moveBy });
        }
    }

    scrollToFirstSelection() {
        const timelineInnerEl = this.timelineInnerRef.current;
        const trackListEl = this.trackListContainerRef.current;
        if (!timelineInnerEl || !trackListEl) {
            return;
        }

        const { selectedSliders } = this.state;
        const { timelineTile } = this.state;
        const { sliders } = timelineTile;

        if (selectedSliders.length === 0) {
            return;
        }

        const selection = selectedSliders[0];
        const slider = sliders[selection.sliderId];

        if (!slider || !slider.enterStart) {
            return;
        }

        let { position } = slider.enterStart;
        if (slider.enterStart.trackPosition) {
            position = slider.enterStart.trackPosition;
        }

        const { scrollLeft, offsetWidth } = timelineInnerEl;
        const { scrollTop, offsetHeight } = trackListEl;
        const sliderX = position.x;
        const sliderY = position.y;
        const bufferDistance = 30;

        if ((scrollLeft + offsetWidth - bufferDistance) < sliderX || sliderX < (scrollLeft + bufferDistance)) {
            const moveBy = sliderX - (scrollLeft + offsetWidth) + offsetWidth * 0.5;
            timelineInnerEl.scrollBy({ left: moveBy });
        }

        if ((scrollTop + offsetHeight - bufferDistance) < sliderY || sliderY < (scrollTop + bufferDistance)) {
            const moveBy = sliderY - (scrollTop + offsetHeight) + offsetHeight * 0.5;
            trackListEl.scrollBy({ top: moveBy });
        }
    }

    handleWindowResize() {
        const { isPlayAll, sideBarMenuWidth, project, timelineMode, } = this.props;
        const { selectedSliders, timelineTile } = this.state;
        const { sliders, stepper, timelinePlot } = timelineTile;
        const { timeScale, excessDuration: stepperExcessDuration, interval } = stepper;

        let setStateCallback;
        const playheadPosition = getTrackPosition({ slider: sliders[playheadId], thumbIds: ["enterStart"] }).enterStart;
        const playheadTime = playheadPosition.step * interval;
        const newTimelinePlot = calculateTimelinePlot({ currentTimelinePlot: timelinePlot, sideBarMenuWidth });
        const newStepper = calculateStepperSize({
            duration: project.get("duration"),
            timelinePlot: newTimelinePlot,
            timeScale,
            excessDuration: stepperExcessDuration,
            interval,
        });
        const newTracks = getTracks({ project, stepper: newStepper, timelineMode, });
        const { sliders: newSliders, timelineSnapPoints: newTimelineSnapPoints } = getSliders({
            playhead: playheadTime,
            project,
            stepper: newStepper,
            tracks: newTracks,
            timelineMode,
        });
        const newSelectedSliders = selectedSliders.filter((selection) => newSliders[selection.sliderId]);
        const newPlayheadThumb = newSliders[playheadId].enterStart;

        // try to keep playhead in same position on screen
        const timelineInnerEl = this.timelineInnerRef.current;
        if (timelineInnerEl) {
            const { scrollLeft, offsetWidth } = timelineInnerEl;

            if (newTimelinePlot.width > timelinePlot.width) {
                // ruler width will increase
                let currentPlayheadPositionX = playheadPosition.x - scrollLeft;
                currentPlayheadPositionX = newTimelinePlot.width * (currentPlayheadPositionX / offsetWidth); // keep proportion to visually keep playhead in same place
                const newPlayheadPositionX = newPlayheadThumb.position.x - scrollLeft;
                setStateCallback = () => timelineInnerEl.scrollBy({ left: newPlayheadPositionX - currentPlayheadPositionX });
            } else if (newTimelinePlot.width < timelinePlot.width) {
                // ruler width will decrease
                const newScrollWidth = RULER_OPTIONS.paddingLeft + newStepper.steps * newStepper.stepSizePx + RULER_OPTIONS.paddingRight;
                let newScrollLeft = scrollLeft;
                if (scrollLeft + newTimelinePlot.width >= newScrollWidth) {
                    newScrollLeft = newScrollWidth - newTimelinePlot.width;
                }
                let currentPlayheadPositionX = playheadPosition.x - scrollLeft;
                currentPlayheadPositionX = newTimelinePlot.width * (currentPlayheadPositionX / offsetWidth); // keep proportion to visually keep playhead in same place
                const newPlayheadPositionX = newPlayheadThumb.position.x - newScrollLeft;
                setStateCallback = () => timelineInnerEl.scrollBy({ left: newPlayheadPositionX - currentPlayheadPositionX });
            }
        }

        this.dispatchTimelineTile(
            {
                type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
                payload: {
                    timelinePlot: newTimelinePlot,
                    stepper: newStepper,
                    tracks: newTracks,
                    sliders: newSliders,
                    timelineSnapPoints: newTimelineSnapPoints,
                },
                updateStorePlayhead: !isPlayAll,
            },
            setStateCallback
        );
        this.setSelectedSliders({ selectedSliders: newSelectedSliders });
        this.props.setTimelineHeight(newTimelinePlot);
    }

    /**
     * handle timeline resize on resize thumb drag
     * @param {MouseDragParams} params
     */
    resizeTimeline(params = {}) {
        const { mouseMovedBy } = params;
        const { project, sideBarMenuWidth, timelineMode, } = this.props;
        const { selectedSliders, timelineTile, timelinePlotBeforeDrag, } = this.state;
        const { sliders, stepper } = timelineTile;
        const { timeScale, excessDuration: stepperExcessDuration, interval, } = stepper;

        const playheadThumb = sliders[playheadId].enterStart;
        const playheadTime = playheadThumb.position.step * interval;
        const newTimelinePlot = calculateTimelinePlot({
            currentTimelinePlot: timelinePlotBeforeDrag,
            mouseMovedBy,
            sideBarMenuWidth,
        });
        const newStepper = calculateStepperSize({
            duration: project.get("duration"),
            timelinePlot: newTimelinePlot,
            timeScale,
            excessDuration: stepperExcessDuration,
            interval,
        });
        const newTracks = getTracks({ project, stepper: newStepper, timelineMode, });
        const { sliders: newSliders, timelineSnapPoints: newTimelineSnapPoints } = getSliders({
            playhead: playheadTime,
            project,
            stepper: newStepper,
            prevSliders: sliders,
            tracks: newTracks,
            timelineMode,
        });
        const newSelectedSliders = selectedSliders.filter((selection) => newSliders[selection.sliderId]);
        this.dispatchTimelineTile({
            type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
            payload: {
                timelinePlot: newTimelinePlot,
                stepper: newStepper,
                tracks: newTracks,
                sliders: newSliders,
                timelineSnapPoints: newTimelineSnapPoints,
            },
        });
        this.props.setTimelineHeight(newTimelinePlot)
        this.setSelectedSliders({ selectedSliders: newSelectedSliders });
    }

    /**
     * @typedef ResizeClickParams
     * @property {"expand" | "minimize" | undefined} clickType
     *
     * handle timeline resize on resize thumb drag end
     * @param {MouseDragEndParams & ResizeClickParams} params
     */
    stopTimelineResize(params = {}) {
        const { clickType, mouseMovedBy } = params;
        const { project, sideBarMenuWidth, timelineMode } = this.props;
        const { selectedSliders, timelineTile, timelinePlotBeforeDrag, } = this.state;
        const { sliders, stepper } = timelineTile;
        const { timeScale, excessDuration: stepperExcessDuration, interval, } = stepper;

        const isButtonClick = clickType === "expand" || clickType === "minimize";

        if (isButtonClick) {
            this.setState({ animateTimelineContainer: true });
        }

        const playheadThumb = sliders[playheadId].enterStart;
        const playheadTime = playheadThumb.position.step * interval;
        const newTimelinePlot = calculateTimelinePlot({
            currentTimelinePlot: !isButtonClick
                ? timelinePlotBeforeDrag
                : undefined,
            mouseMovedBy: !isButtonClick ? mouseMovedBy : undefined,
            isExpand: clickType === "expand",
            isMinimize: clickType === "minimize",
            sideBarMenuWidth,
        });
        const newStepper = calculateStepperSize({
            duration: project.get("duration"),
            timelinePlot: newTimelinePlot,
            timeScale,
            excessDuration: stepperExcessDuration,
            interval,
        });
        const newTracks = getTracks({ project, stepper: newStepper, timelineMode, });
        const { sliders: newSliders, timelineSnapPoints: newTimelineSnapPoints } = getSliders({
            playhead: playheadTime,
            project,
            stepper: newStepper,
            prevSliders: sliders,
            tracks: newTracks,
            timelineMode,
        });
        const newSelectedSliders = selectedSliders.filter((selection) => newSliders[selection.sliderId]);
        this.dispatchTimelineTile({
            type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
            payload: {
                timelinePlot: newTimelinePlot,
                stepper: newStepper,
                tracks: newTracks,
                sliders: newSliders,
                timelineSnapPoints: newTimelineSnapPoints,
            },
        });
        this.props.setTimelineHeight(newTimelinePlot);
        this.setSelectedSliders({ selectedSliders: newSelectedSliders });
    }

    handleResizeMouseDown(e) {
        const { timelineTile } = this.state;
        const { timelinePlot } = timelineTile;
        this.setState({ timelinePlotBeforeDrag: timelinePlot });
        this.props.initiateTimelineResize({
            event: e,
            throttle: (1 / 120) * 1000,
            onMouseDrag: this.resizeTimeline,
            onMouseDragEnd: this.stopTimelineResize,
        });
    }

    /**
     * decides how much duration to show on screen
     * @param {React.ChangeEvent<HTMLInputElement> | null} event
     * @param {number} scale
     */
    changeTimeScale(event, scale) {
        const { project, timelineMode, } = this.props;
        const { selectedSliders, timelineTile, } = this.state;
        const { sliders, stepper, timelinePlot } = timelineTile;

        let setStateCallback;
        let newTimeScale = scale;
        // if (event) {
        //     newTimeScale = parseFloat(event.target.value);
        // }
        if (!Number.isFinite(newTimeScale)) {
            newTimeScale = RULER_OPTIONS.timeScale.default;
        }
        if (newTimeScale < RULER_OPTIONS.timeScale.min) {
            newTimeScale = RULER_OPTIONS.timeScale.min;
        }
        if (newTimeScale > RULER_OPTIONS.timeScale.max) {
            newTimeScale = RULER_OPTIONS.timeScale.max;
        }

        const playheadThumb = sliders[playheadId].enterStart;
        const playheadTime = playheadThumb.position.step * stepper.interval;
        const newStepper = calculateStepperSize({
            duration: project.get("duration"),
            timelinePlot,
            timeScale: newTimeScale,
            interval: stepper.interval,
        });
        const newTracks = getTracks({ project, stepper: newStepper, timelineMode, });
        const { sliders: newSliders, timelineSnapPoints: newTimelineSnapPoints } = getSliders({
            playhead: playheadTime,
            project,
            stepper: newStepper,
            prevSliders: sliders,
            tracks: newTracks,
            timelineMode,
        });
        const newSelectedSliders = selectedSliders.filter((selection) => newSliders[selection.sliderId]);
        const newPlayheadThumb = newSliders[playheadId].enterStart;

        // try to keep playhead in same position on screen
        const timelineInnerEl = this.timelineInnerRef.current;
        if (timelineInnerEl) {
            const { scrollLeft, offsetWidth } = timelineInnerEl;
            if (newTimeScale > stepper.timeScale && newTimeScale >= 0) {
                // zooming in and can scroll
                const currentPlayheadPositionX = playheadThumb.position.x - scrollLeft;
                const newPlayheadPositionX = newPlayheadThumb.position.x - scrollLeft;
                setStateCallback = () => timelineInnerEl.scrollBy({ left: newPlayheadPositionX - currentPlayheadPositionX });
            } else if (newTimeScale < stepper.timeScale && newTimeScale >= 0) {
                // zooming out and can scroll
                const newScrollWidth = RULER_OPTIONS.paddingLeft + newStepper.steps * newStepper.stepSizePx + RULER_OPTIONS.paddingRight;
                let newScrollLeft = scrollLeft;
                if (scrollLeft + offsetWidth >= newScrollWidth) {
                    newScrollLeft = newScrollWidth - offsetWidth;
                }
                const currentPlayheadPositionX = playheadThumb.position.x - scrollLeft;
                const newPlayheadPositionX = newPlayheadThumb.position.x - newScrollLeft;
                setStateCallback = () => timelineInnerEl.scrollBy({ left: newPlayheadPositionX - currentPlayheadPositionX });
            }
        }

        this.dispatchTimelineTile(
            {
                type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
                payload: {
                    stepper: newStepper,
                    tracks: newTracks,
                    sliders: newSliders,
                    timelineSnapPoints: newTimelineSnapPoints,
                },
            },
            setStateCallback
        );
        this.setSelectedSliders({ selectedSliders: newSelectedSliders });
    }

    /**
     * handle ruler auto scroll
     * @param {AutoScrollCallbackParams} params
     */
    onTimelineAutoScroll(params = {}) {
        const { scrollStatus } = params;
        let { scrollLeft, scrollTop, scrollLeftCountAfterEnd } = params;

        const { selectedSliders, timelineTile, timelineAutoScrollData } = this.state;
        const { sliders, stepper, tracks, timelineSnapPoints } = timelineTile;

        const scrollOptions = getScrollOptions({ selectedSliders, sliders });
        let updatedStepper = stepper;
        let updatedTracks = tracks;
        let updatedSliders = sliders;
        /** @type {MatchingSnapPoints | undefined} */
        let matchingSnapPoints;

        if (
            timelineAutoScrollData
            && timelineAutoScrollData.mousePosition
            && timelineAutoScrollData.mouseDownPosition
            && scrollOptions
        ) {
            let { scrollLeftOnMouseDown, scrollTopOnMouseDown } =
                timelineAutoScrollData;
            if (!scrollLeftOnMouseDown) {
                scrollLeftOnMouseDown = 0;
            }
            if (!scrollTopOnMouseDown) {
                scrollTopOnMouseDown = 0;
            }
            if (scrollLeft === undefined) {
                if (this.timelineInnerRef.current) {
                    scrollLeft = this.timelineInnerRef.current.scrollLeft;
                } else {
                    scrollLeft = 0;
                }
            }
            if (scrollTop === undefined) {
                if (this.trackListContainerRef.current) {
                    scrollTop = this.trackListContainerRef.current.scrollTop;
                } else {
                    scrollTop = 0;
                }
            }
            if (scrollLeftCountAfterEnd === undefined) {
                scrollLeftCountAfterEnd = 0;
            }

            if (
                scrollStatus.isHorizontalScroll
                && scrollLeftCountAfterEnd > 0
                && shouldExtendRuler({ selectedSliders, sliders: updatedSliders, stepper: updatedStepper })
            ) {
                let increaseStepBy = Math.floor(scrollStatus.scrollLeftDisPerInterval / stepper.stepSizePx);
                if (increaseStepBy === 0) {
                    increaseStepBy = 1;
                }
                const videoExcessSteps = updatedStepper.videoExcessSteps + increaseStepBy;
                /** @type {StepperState} */
                updatedStepper = {
                    // to increase ruler's space
                    ...updatedStepper,
                    videoExcessSteps,
                };

                /** @type {Slider} */
                const updatedRulerSlider = {
                    // increase ruler's extreme end to give space for video slider to increase
                    ...updatedSliders[rulerId],
                    rulerEnd: {
                        ...updatedSliders[rulerId].rulerEnd,
                        position: {
                            ...updatedSliders[rulerId].rulerEnd.position,
                            step: updatedStepper.steps + updatedStepper.videoExcessSteps,
                            x: (
                                RULER_OPTIONS.paddingLeft
                                + (updatedStepper.steps + updatedStepper.videoExcessSteps) * updatedStepper.stepSizePx
                            ),
                        },
                    },
                };

                updatedSliders = {
                    ...updatedSliders,
                    [rulerId]: updatedRulerSlider,
                };
                updatedTracks = updateTrackWidth({ stepper: updatedStepper, tracks });
            }

            const moveToTrackResult = moveToTrack({
                moveTo: {
                    x: scrollLeft + timelineAutoScrollData.mousePosition.x,
                    y: scrollTop + timelineAutoScrollData.mousePosition.y,
                },
                startedFrom: {
                    x: scrollLeftOnMouseDown + timelineAutoScrollData.mouseDownPosition.x,
                    y: scrollTopOnMouseDown + timelineAutoScrollData.mouseDownPosition.y,
                },
                moveType: "drag",
                selectedSliders,
                sliders: updatedSliders,
                tracks,
            });
            updatedSliders = moveToTrackResult.updatedSliders;
            const moveSlidersResult = moveSliders({
                moveTo: {
                    x: scrollLeft + timelineAutoScrollData.mousePosition.x,
                    y: scrollTop + timelineAutoScrollData.mousePosition.y,
                },
                startedFrom: {
                    x: (
                        scrollLeftOnMouseDown
                        + timelineAutoScrollData.mouseDownPosition.x
                    ),
                    y: scrollTopOnMouseDown + timelineAutoScrollData.mouseDownPosition.y,
                },
                moveType: "drag",
                scrollType: "scrolling",
                selectedSliders,
                tracks: updatedTracks,
                sliders: updatedSliders,
                stepper: updatedStepper,
                timelineSnapPoints,
            });
            updatedSliders = moveSlidersResult.sliders;
            matchingSnapPoints = moveSlidersResult.matchingSnapPoints;
        }

        /** @type {MergeTimelineTilePayload} */
        const tileUpdate = {
            sliders: updatedSliders,
            tracks: updatedTracks,
            stepper: updatedStepper,
        };
        if (matchingSnapPoints) {
            tileUpdate.matchingSnapPoints = matchingSnapPoints;
        }

        this.dispatchTimelineTile({
            type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
            payload: tileUpdate,
        });
    }

    /**
     * places playhead thumb based on mouse event
     * @param {MouseDragParams} params
     */
    handleSlidersDrag(params = {}) {
        const { event, mouseMovedBy, mouseCurrentPosition, mouseDownPosition, scrollLeftOnMouseDown, scrollTopOnMouseDown } = params;
        event.preventDefault(); // to prevent built autoscroll in safari

        const DRAG_START_THRESHOLD = this.dragStartThreshold;
        if (
            !this.sliderMoved
            && (Math.abs(mouseMovedBy.x) > DRAG_START_THRESHOLD || Math.abs(mouseMovedBy.y) > DRAG_START_THRESHOLD)
        ) {
            this.sliderMoved = true;
            this.setState({ sliderMoved: true });
        }
        if (!this.sliderMoved) {
            return;
        }
        this.isDraggingActive = true;

        const { timelineScrollStatus, startTimelineScroll, stopTimelineScroll } = this.props;
        const { selectedSliders, timelineTile } = this.state;
        const { sliders, stepper, timelinePlot, timelineSnapPoints, tracks } = timelineTile;

        /** @type {StartScrollParams} */
        const timelineScrollParams = {};
        const scrollOptions = getScrollOptions({ selectedSliders, sliders });

        let isThumbDrag = false;
        if (selectedSliders.length === 1 && selectedSliders[0].thumbId) {
            isThumbDrag = true;
        }

        if (scrollOptions) {
            const adjustedMousePosition = { ...mouseCurrentPosition };
            const adjustedMouseDiff = { ...mouseMovedBy };
            const adjustedMouseDownPosition = { ...mouseDownPosition };
            let scrollLeft = 0;
            let scrollTop = 0;
            let willRulerScroll = false;

            if (this.timelineInnerRef.current instanceof HTMLElement && this.trackListContainerRef.current instanceof HTMLElement) {
                // adjust mouse position to prevent playhead jump on scroll
                const thresholdX = scrollOptions.clientExtremeThreshold;
                const thresholdY = scrollOptions.clientExtremeThreshold;
                const dragBounds = {
                    x: thresholdX,
                    y: thresholdY,
                    width: timelinePlot.width - 2 * thresholdX,
                    height: timelinePlot.height - tracksOffsetY - 2 * thresholdY,
                };
                scrollLeft = this.timelineInnerRef.current.scrollLeft;
                scrollTop = this.trackListContainerRef.current.scrollTop;

                const scrollLeftToRight = (
                    mouseMovedBy.x < 0 // moved towards left
                    && scrollLeft > 0 // can scroll towards left
                    && mouseCurrentPosition.x <= dragBounds.x // moved above threshold
                );
                const scrollRightToLeft = (
                    mouseMovedBy.x > 0 // moved towards left
                    && ( // can scroll towards right
                        shouldExtendRuler({ selectedSliders, sliders, stepper })
                        || (scrollLeft + this.timelineInnerRef.current.offsetWidth < this.timelineInnerRef.current.scrollWidth)
                    ) && mouseCurrentPosition.x >= dragBounds.x + dragBounds.width // moved above threshold
                );

                const scrollTopToBottom = (
                    !isThumbDrag
                    && mouseMovedBy.y < 0 // moved towards top
                    && scrollTop > 0 // can scroll towards top
                    && mouseCurrentPosition.y <= dragBounds.y // moved above threshold
                );
                const scrollBottomToTop = (
                    !isThumbDrag
                    && mouseMovedBy.y > 0 // moved towards bottom
                    // can scroll towards bottom
                    && (scrollTop + this.trackListContainerRef.current.offsetHeight < this.trackListContainerRef.current.scrollHeight)
                    && mouseCurrentPosition.y >= dragBounds.y + dragBounds.height // moved above threshold
                );

                if (scrollLeftToRight || scrollRightToLeft) {
                    let scrollBy = scrollOptions.moveBy;
                    // block playhead within client bounds to prevent playhead from moving far from the screen
                    if (scrollLeftToRight) {
                        adjustedMousePosition.x = dragBounds.x;
                        if (mouseDownPosition.x < adjustedMousePosition.x) { // mouse clicked after threshold
                            adjustedMouseDownPosition.x = adjustedMousePosition.x + 1;
                        }
                        adjustedMouseDiff.x = adjustedMousePosition.x - adjustedMouseDownPosition.x;
                        scrollBy = -scrollBy; // change scroll direction to left
                    } else if (scrollRightToLeft) {
                        adjustedMousePosition.x = dragBounds.x + dragBounds.width;
                        if (mouseDownPosition.x > adjustedMousePosition.x) { // mouse clicked after threshold
                            adjustedMouseDownPosition.x = adjustedMousePosition.x - 1;
                        }
                        adjustedMouseDiff.x = adjustedMousePosition.x - adjustedMouseDownPosition.x;
                    }

                    timelineScrollParams.interval = scrollOptions.interval;
                    timelineScrollParams.scrollLeftDisPerInterval = scrollBy;
                    timelineScrollParams.isHorizontalScroll = true;
                    willRulerScroll = true;
                }

                if (scrollTopToBottom || scrollBottomToTop) {
                    let scrollBy = scrollOptions.moveBy;
                    // block slider within client bounds to prevent slider from moving far from the screen
                    if (scrollTopToBottom) {
                        adjustedMousePosition.y = dragBounds.y;
                        if (mouseDownPosition.y < adjustedMousePosition.y) { // mouse clicked after threshold
                            adjustedMouseDownPosition.y = adjustedMousePosition.y + 1;
                        }
                        adjustedMouseDiff.y = adjustedMousePosition.y - adjustedMouseDownPosition.y;
                        scrollBy = -scrollBy; // change scroll direction to left
                    } else if (scrollBottomToTop) {
                        adjustedMousePosition.y = dragBounds.y + dragBounds.height;
                        if (mouseDownPosition.y > adjustedMousePosition.y) { // mouse clicked after threshold
                            adjustedMouseDownPosition.y = adjustedMousePosition.y - 1;
                        }
                        adjustedMouseDiff.y = adjustedMousePosition.y - adjustedMouseDownPosition.y;
                    }

                    timelineScrollParams.interval = scrollOptions.interval;
                    timelineScrollParams.scrollTopDisPerInterval = scrollBy;
                    timelineScrollParams.isVerticalScroll = true;
                }

                let updateScrollData = false;
                if (timelineScrollStatus.isScrolling) {
                    const {
                        isHorizontalScroll = false,
                        isVerticalScroll = false,
                        scrollLeftDisPerInterval,
                        scrollTopDisPerInterval,
                    } = timelineScrollParams;

                    if (!isHorizontalScroll && !isVerticalScroll) { // will not scroll in both x and y axis
                        stopTimelineScroll();
                    } else if (
                        isHorizontalScroll !== timelineScrollStatus.isHorizontalScroll
                        || isVerticalScroll !== timelineScrollStatus.isVerticalScroll
                        || (isHorizontalScroll && scrollLeftDisPerInterval !== timelineScrollStatus.scrollLeftDisPerInterval)
                        || (isVerticalScroll && scrollTopDisPerInterval !== timelineScrollStatus.scrollTopDisPerInterval)
                    ) { // calculated scroll status is different from existing scroll status
                        /** @type {StartScrollParams} */
                        const startScrollParams = {
                            scrollDeps: {
                                onAutoScroll: this.onTimelineAutoScroll,
                                scrollLeftElRef: this.timelineInnerRef,
                                scrollTopElRef: this.trackListContainerRef,
                            },
                            scrollParams: timelineScrollParams,
                        };
                        startTimelineScroll(startScrollParams);
                        updateScrollData = true;
                    } else if (
                        (isHorizontalScroll && timelineScrollStatus.isHorizontalScroll) ||
                        (isVerticalScroll && timelineScrollStatus.isVerticalScroll)
                    ) { // continue scrolling without changing scroll state
                        updateScrollData = true;
                    }
                } else if (timelineScrollParams.isHorizontalScroll || timelineScrollParams.isVerticalScroll) {
                    /** @type {StartScrollParams} */
                    const startScrollParams = {
                        scrollDeps: {
                            onAutoScroll: this.onTimelineAutoScroll,
                            scrollLeftElRef: this.timelineInnerRef,
                            scrollTopElRef: this.trackListContainerRef,
                        },
                        scrollParams: timelineScrollParams,
                    };
                    startTimelineScroll(startScrollParams);
                    updateScrollData = true;
                }

                if (updateScrollData) {
                    this.setState({
                        timelineAutoScrollData: {
                            mouseDownPosition: adjustedMouseDownPosition,
                            mousePosition: adjustedMousePosition,
                            mouseMovedBy: adjustedMouseDiff,
                            scrollLeftOnMouseDown,
                            scrollTopOnMouseDown,
                        },
                    });
                }
            }

            let updatedSliders = sliders;
            /** @type {MatchingSnapPoints | undefined} */
            let matchingSnapPoints;
            if (!timelineScrollParams.isVerticalScroll) {
                const moveToTrackResult = moveToTrack({
                    moveTo: {
                        x: scrollLeft + adjustedMousePosition.x,
                        y: scrollTop + adjustedMousePosition.y,
                    },
                    startedFrom: {
                        x: scrollLeftOnMouseDown + adjustedMouseDownPosition.x,
                        y: scrollTopOnMouseDown + adjustedMouseDownPosition.y,
                    },
                    moveType: "drag",
                    selectedSliders,
                    sliders,
                    tracks,
                });
                updatedSliders = moveToTrackResult.updatedSliders;
            }
            if (!timelineScrollParams.isHorizontalScroll) {
                const moveSlidersResult = moveSliders({
                    moveTo: {
                        x: scrollLeft + adjustedMousePosition.x,
                        y: scrollTop + adjustedMousePosition.y,
                    },
                    startedFrom: {
                        x: scrollLeftOnMouseDown + adjustedMouseDownPosition.x,
                        y: scrollTopOnMouseDown + adjustedMouseDownPosition.y,
                    },
                    moveType: "drag",
                    scrollType: willRulerScroll ? "scrolling" : "not-scrolling",
                    selectedSliders,
                    tracks,
                    sliders: updatedSliders,
                    stepper,
                    timelineSnapPoints,
                });
                updatedSliders = moveSlidersResult.sliders;
                matchingSnapPoints = moveSlidersResult.matchingSnapPoints;
            }
            /** @type {MergeTimelineTilePayload} */
            const tileUpdate = { sliders: updatedSliders };
            if (matchingSnapPoints) {
                tileUpdate.matchingSnapPoints = matchingSnapPoints;
            }



            this.dispatchTimelineTile({
                type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
                payload: tileUpdate,
                updateStorePlayhead: true,
            });
        }
    }

    isOverlapping = (enterStart, exitEnd, itemEnterStart, itemExitEnd) => {
        return (
            (enterStart >= itemEnterStart && exitEnd <= itemExitEnd) || // Completely contains item
            (enterStart <= itemEnterStart && exitEnd >= itemExitEnd) || // Completely inside item
            (enterStart <= itemEnterStart && exitEnd > itemEnterStart) || // Overlaps with beginning of item
            (enterStart < itemExitEnd && exitEnd >= itemExitEnd) // Overlaps with end of item
        );
    };

    /**
     * Rebuilds sliders from the current project data.
     * Used to revert sliders to their original position when a drag
     * is blocked (e.g., overlap with locked items).
     */
    _revertSlidersToProject() {
        const { project, timelineMode } = this.props;
        const { timelineTile } = this.state;
        const { sliders, stepper, timelinePlot } = timelineTile;

        let playheadTime = 0;
        if (sliders && sliders[playheadId] && sliders[playheadId].enterStart) {
            const playheadThumb = sliders[playheadId].enterStart;
            playheadTime = playheadThumb.position.step * stepper.interval;
            if (playheadTime > project.get("duration")) {
                playheadTime = project.get("duration");
            }
        }

        const newStepper = adjustTimeScale({
            newProject: project, stepper, timelinePlot,
            interval: this.props.interval,
        });
        const newTracks = getTracks({ project, stepper: newStepper, timelineMode });
        const { sliders: newSliders, timelineSnapPoints: newTimelineSnapPoints } = getSliders({
            playhead: playheadTime,
            project,
            stepper: newStepper,
            tracks: newTracks,
            timelineMode,
        });

        this.dispatchTimelineTile({
            type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
            payload: {
                stepper: newStepper,
                tracks: newTracks,
                sliders: newSliders,
                timelineSnapPoints: newTimelineSnapPoints,
                matchingSnapPoints: {},
            },
        });
    }

    /**
     * places playhead thumb based on mouse event
     * @param {MouseDragParams} params
     */
    handleSlidersDragEnd(params = {}) {
        const {
            mouseCurrentPosition,
            mouseDownPosition,
            scrollLeftOnMouseDown,
            scrollTopOnMouseDown,
        } = params;

        const { project, timelineScrollStatus, stopTimelineScroll, cleanAudio } = this.props;
        const { selectedSliders, timelineAutoScrollData, timelineTile } = this.state;
        const { sliders, stepper, timelineSnapPoints, tracks } = timelineTile;

        stopTimelineScroll();
        this.setState({ timelineAutoScrollData: null });

        if (!this.sliderMoved) {
            return;
        }

        this.isDraggingActive = false;
        // this.sliderMoved = false; // NOTE: do not set this flag to false on mouseup/click. this flag is being used by other mouseup events
        this.setState({ sliderMoved: false });

        /** @type {MergeTimelineTilePayload} */
        const tileUpdate = {};
        let updatedSliders = sliders;
        const scrollOptions = getScrollOptions({ selectedSliders, sliders: updatedSliders });
        /** @type {MatchingSnapPoints} */
        let matchingSnapPoints = {};
        let scrollLeft = 0;
        if (this.timelineInnerRef.current instanceof HTMLElement) {
            scrollLeft = this.timelineInnerRef.current.scrollLeft;
        }
        let scrollTop = 0;
        if (this.trackListContainerRef.current instanceof HTMLElement) {
            scrollTop = this.trackListContainerRef.current.scrollTop;
        }

        if (scrollOptions) {
            if (timelineScrollStatus.isScrolling) {
                if (
                    timelineAutoScrollData
                    && timelineAutoScrollData.mousePosition
                    && timelineAutoScrollData.mouseDownPosition
                    && scrollOptions
                ) {
                    const moveToTrackResult = moveToTrack({
                        moveTo: {
                            x: scrollLeft + timelineAutoScrollData.mousePosition.x,
                            y: scrollTop + timelineAutoScrollData.mousePosition.y,
                        },
                        startedFrom: {
                            x: scrollLeftOnMouseDown + timelineAutoScrollData.mouseDownPosition.x,
                            y: scrollTopOnMouseDown + timelineAutoScrollData.mouseDownPosition.y,
                        },
                        moveType: "place",
                        selectedSliders,
                        sliders: updatedSliders,
                        tracks,
                    });
                    updatedSliders = moveToTrackResult.updatedSliders;
                    const moveSlidersResult = moveSliders({
                        moveTo: {
                            x: scrollLeft + timelineAutoScrollData.mousePosition.x,
                            y: scrollTop + timelineAutoScrollData.mousePosition.y,
                        },
                        startedFrom: {
                            x: scrollLeftOnMouseDown + timelineAutoScrollData.mouseDownPosition.x,
                            y: scrollTopOnMouseDown + timelineAutoScrollData.mouseDownPosition.y,
                        },
                        moveType: "place",
                        scrollType: "scroll-end",
                        selectedSliders,
                        tracks,
                        sliders: updatedSliders,
                        stepper,
                        timelineSnapPoints,
                    });
                    updatedSliders = moveSlidersResult.sliders;
                    matchingSnapPoints = moveSlidersResult.matchingSnapPoints;
                }
            } else {
                const moveToTrackResult = moveToTrack({
                    moveTo: {
                        x: scrollLeft + mouseCurrentPosition.x,
                        y: scrollTop + mouseCurrentPosition.y,
                    },
                    startedFrom: {
                        x: scrollLeftOnMouseDown + mouseDownPosition.x,
                        y: scrollTopOnMouseDown + mouseDownPosition.y,
                    },
                    moveType: "place",
                    selectedSliders,
                    sliders: updatedSliders,
                    tracks,
                });
                updatedSliders = moveToTrackResult.updatedSliders;
                const moveSlidersResult = moveSliders({
                    moveTo: {
                        x: scrollLeft + mouseCurrentPosition.x,
                        y: scrollTop + mouseCurrentPosition.y,
                    },
                    startedFrom: {
                        x: scrollLeftOnMouseDown + mouseDownPosition.x,
                        y: scrollTopOnMouseDown + mouseDownPosition.y,
                    },
                    moveType: "place",
                    scrollType: "not-scrolling",
                    selectedSliders,
                    tracks,
                    sliders: updatedSliders,
                    stepper,
                    timelineSnapPoints,
                });
                updatedSliders = moveSlidersResult.sliders;
                matchingSnapPoints = moveSlidersResult.matchingSnapPoints;
            }
        }

        if (updatedSliders[playheadId] !== sliders[playheadId]) {
            const updatedTimelineSnapPoints = removeDefaultSnapPoints({
                timelineSnapPoints,
            });
            addDefaultSnapPoints({
                mutableTimelineSnapPoints: updatedTimelineSnapPoints, // it is okay to use mutation logic here as we have removed whole trace of default sliders
                sliders: updatedSliders,
            });
            tileUpdate.timelineSnapPoints = updatedTimelineSnapPoints;
        }

        if (stepper.videoExcessSteps) {
            const updatedStepper = {
                ...stepper,
                videoExcessSteps: 0,
            };

            /** @type {Slider} */
            const updatedRulerSlider = {
                // increase ruler's extreme end to give space for video slider to increase
                ...updatedSliders[rulerId],
                rulerEnd: {
                    ...updatedSliders[rulerId].rulerEnd,
                    position: {
                        ...updatedSliders[rulerId].rulerEnd.position,
                        step: updatedStepper.steps + updatedStepper.videoExcessSteps,
                        x: RULER_OPTIONS.paddingLeft + (updatedStepper.steps + updatedStepper.videoExcessSteps) * updatedStepper.stepSizePx,
                    },
                },
            };

            updatedSliders = {
                ...updatedSliders,
                [rulerId]: updatedRulerSlider,
            };

            tileUpdate.stepper = updatedStepper;
            tileUpdate.tracks = updateTrackWidth({ stepper: updatedStepper, tracks });
        }

        tileUpdate.sliders = updatedSliders;
        tileUpdate.matchingSnapPoints = matchingSnapPoints;

        this.dispatchTimelineTile({ type: TIMELINE_TILE_ACTIONS.MERGE_STATE, payload: tileUpdate });
        const toUpdate = getUpdatedTimeline({
            project,
            selectedSliders,
            sliders: updatedSliders,
            stepper,
            appReframeData: this.props.appReframeData
        });
        // The check prevents timeline overlap.
        let isAvoidOverlap = false;
        if (toUpdate.length) {


            PROJECT_CONTAINERS.some((container) => {
                if (!project.get(container)) return false;

                // Find the main update that contains time changes
                const mainUpdate = toUpdate.find(u => u.toUpdate &&
                    (u.toUpdate.enterStart !== undefined ||
                        u.toUpdate.exitEnd !== undefined ||
                        u.toUpdate.playStart !== undefined ||
                        u.toUpdate.playEnd !== undefined));
                if (!mainUpdate) return false;

                const isEmpty = Object.keys(mainUpdate.toUpdate || {}).length === 0;
                if (isEmpty) {
                    isAvoidOverlap = true;
                    return true;
                }

                const toUpdateTrack = mainUpdate.toUpdate.track;
                const enterStart = mainUpdate.toUpdate.enterStart || mainUpdate.toUpdate.playStart;
                const exitEnd = mainUpdate.toUpdate.exitEnd || mainUpdate.toUpdate.playEnd;
                const selectedItem = this.props.project.getIn([container, mainUpdate.id]);

                // Special handling for "audios"
                if (container === "audios") {
                    return project.get(container).some((item) => {
                        // Skip checking overlap against the item being moved itself
                        if (toUpdate.some(u => u.id === item.get("id"))) {
                            return false;
                        }
                        // To prevent overlapping while noise removal is in progress
                        if (cleanAudio.get("id") !== item.get("id")) return false;
                        const itemTrack = item.get("track");
                        const itemEnterStart = item.get("playStart");
                        const itemExitEnd = item.get("playEnd");
                        if ((itemTrack === toUpdateTrack || (itemTrack === selectedItem?.get("track") && toUpdateTrack === undefined)) &&
                            this.isOverlapping(enterStart, exitEnd, itemEnterStart, itemExitEnd)) {
                            toUpdate.forEach(item => {
                                item.toUpdate = {};
                            });
                            isAvoidOverlap = true;
                            return true;
                        }
                        return false;
                    });
                }

                return project.get(container).some((item) => {
                    // Skip checking overlap against the item being moved itself
                    if (toUpdate.some(u => u.id === item.get("id"))) {
                        return false;
                    }
                    const enterId = item.get("transitionEnterId");
                    if (enterId && enterId !== "none") {
                        const data = this.props.project.getIn(["workspaceItems", enterId]);
                        if (!(data && data.get("id"))) {
                            const data = {
                                container: "workspaceItems",
                                id: item.get("id"),
                                toUpdate: {
                                    transitionEnterId: "none",
                                }
                            }
                            toUpdate.push(data);
                        }
                    }

                    const exitId = item.get("transitionExitId");
                    if (exitId && exitId !== "none") {
                        const data = this.props.project.getIn(["workspaceItems", exitId]);
                        if (!(data && data.get("id"))) {
                            const data = {
                                container: "workspaceItems",
                                id: item.get("id"),
                                toUpdate: {
                                    transitionExitId: "none",
                                }
                            }
                            toUpdate.push(data);
                        }
                    }

                    if ((!item?.get("isLock") || !toUpdate.length) && cleanAudio.get("id") !== item.get("id")) {
                        return false;
                    }

                    const itemTrack = item.get("track");
                    const itemEnterStart = item.get("enterStart");
                    const itemExitEnd = item.get("exitEnd");
                    if ((itemTrack === toUpdateTrack || (itemTrack === selectedItem?.get("track") && toUpdateTrack === undefined)) &&
                        this.isOverlapping(enterStart, exitEnd, itemEnterStart, itemExitEnd)) {
                        toUpdate.forEach(item => {
                            item.toUpdate = {};
                        });
                        isAvoidOverlap = true;
                        return true;
                    }
                    return false;
                });
            });
            if (!isAvoidOverlap) {
                this.props.updateTimelineTime({ toUpdate });
            } else {
                this.props.setTimelineCurrentState({ toUpdate });
                this._revertSlidersToProject();
            }
            // NOTE: reframeData dispatch removed — autosave helper handles scene sync
        }
    }

    updateStorePlayhead(sliders, stepper) {
        if (!sliders) {
            sliders = this.state.timelineTile.sliders;
        }
        if (!stepper) {
            stepper = this.state.timelineTile.stepper;
        }
        const playheadSlider = sliders[playheadId];

        const position = getTrackPosition({ slider: playheadSlider, thumbIds: ["enterStart"] }).enterStart;
        const playheadTime = position.step * stepper.interval;
        if (this.props.isPlayAll || playheadTime !== this.props.playhead) {
            const selectedItemId = this.props.selectedItems?.toJS()[0];
            const workspaceItems = this.props.project.get("workspaceItems");
            const selectedItem = workspaceItems?.get(selectedItemId);

            if (workspaceItems && selectedItem) {
                const hasRestrict = workspaceItems.some(item => {
                    if (!item.get("isRestrict")) return false;

                    const enterStart = item.get("enterStart");
                    const videoEnd = item.get("videoEnd");
                    const maxDuration = item.get("maxDuration");
                    const exitEnd = enterStart + videoEnd;

                    return playheadTime >= exitEnd && playheadTime <= maxDuration;
                });

                if (hasRestrict && selectedItem?.get("isRestrict")) {
                    this.props.showUpgrades({ upgradesource: "Avatar-timeline-restriction" });
                }
            }
            this.props.setPlayHeadTime({ playhead: playheadTime });
        }

    }

    /**
     * handle playhead indicator position on mouse move
     * @param {PointerEvent | React.PointerEvent} event
     */
    movePlayheadIndication(event) {
        const { timelineTile } = this.state;
        const { sliders, stepper, timelinePlot } = timelineTile;

        const mousePosition = getMouseClientPosition(event);
        const playheadIndicatorThumb = sliders[indicatorId].enterStart;
        const playheadThumb = sliders[playheadId].enterStart;
        let scrollLeft = 0;
        if (this.timelineInnerRef.current instanceof HTMLElement) {
            scrollLeft = this.timelineInnerRef.current.scrollLeft;
        }
        const { matchingSnapPoints, sliders: updatedSliders } = moveSliders({
            moveTo: { x: scrollLeft + mousePosition.x - timelinePlot.x, y: 0 },
            startedFrom: { x: playheadIndicatorThumb.position.x },
            moveType: "drag",
            scrollType: "not-scrolling",
            selectedSliders: [
                { sliderId: playheadIndicatorThumb.sliderId, thumbId: playheadIndicatorThumb.id },
            ], // indicator cannot be selected, so make it up
            sliders,
            stepper,
        });
        let isVisible = true;
        const PLAYHEAD_WIDTH = 30;
        const position = getTrackPosition({ slider: updatedSliders[indicatorId], thumbIds: ["enterStart"] }).enterStart;
        if (
            !this.props.isPlayAll
            && playheadThumb.position.step === position.step
            && Math.abs(playheadThumb.position.x - position.x) < PLAYHEAD_WIDTH / 2
        ) {
            isVisible = false;
        }
        updatedSliders[indicatorId].isVisible = isVisible; // we don't have to spread here as moveSliders returns shallow copy of selected slider and selected thumb
        this.dispatchTimelineTile({
            type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
            payload: {
                sliders: updatedSliders,
                matchingSnapPoints,
            },
            updateStorePlayhead: false,
        });
        return updatedSliders;
    }

    /**
     * Function to handle mouse move over timeline ruler and tracks
     * @param {React.PointerEvent} event
     */
    handleTimelineMouseMove(event) {
        const { isPlayAll, timelineMode, } = this.props;
        const { selectedSliders, timelineTile } = this.state;
        const { timelinePlot, tracks } = timelineTile;

        const mousePosition = getMouseClientPosition(event);
        let scrollLeft = 0;
        if (this.timelineInnerRef.current instanceof HTMLElement) {
            scrollLeft = this.timelineInnerRef.current.scrollLeft;
        }
        let scrollTop = 0;
        if (this.trackListContainerRef.current instanceof HTMLElement) {
            scrollTop = this.trackListContainerRef.current.scrollTop;
        }

        let updatedSliders = this.movePlayheadIndication(event);
        if (!isPlayAll) {
            updatedSliders = addGapSlider({
                mousePosition: {
                    x: scrollLeft + mousePosition.x - timelinePlot.x,
                    y: scrollTop + mousePosition.y - (timelinePlot.y + tracksOffsetY),
                },
                sliders: updatedSliders,
                selectedSliders,
                tracks,
                timelineMode,
            }).updatedSliders;
        }

        this.dispatchTimelineTile({
            type: TIMELINE_TILE_ACTIONS.SET_SLIDERS,
            payload: updatedSliders,
            updateStorePlayhead: false,
        });
    }

    /**
     * handle hide playhead indicator
     * @param {object} params
     * @param {Sliders} params.updatedSliders can be passed if this func is called along with other slider update functions
     */
    hidePlayheadIndication(params = {}) {
        let { updatedSliders } = params;
        const { timelineTile } = this.state;
        const { sliders } = timelineTile;

        if (!updatedSliders) {
            updatedSliders = sliders;
        }
        if (updatedSliders[indicatorId].isVisible) {
            updatedSliders = {
                ...updatedSliders,
                [indicatorId]: {
                    ...updatedSliders[indicatorId],
                    isVisible: false,
                },
            };
            this.dispatchTimelineTile({
                type: TIMELINE_TILE_ACTIONS.SET_SLIDERS,
                payload: updatedSliders,
                updateStorePlayhead: false,
            });
        }

        return updatedSliders;
    }

    handleTimelineMouseLeave() {
        let updatedSliders = this.hidePlayheadIndication();
        const { selectedSliders } = this.state;

        for (const sliderId of Reflect.ownKeys(updatedSliders)) {
            const slider = updatedSliders[sliderId];
            if (slider && slider.sliderType === SLIDER_TYPES.GAP) {
                const isSelected = selectedSliders.some((selection) => selection.sliderId === sliderId);
                if (!isSelected) {
                    updatedSliders = { ...updatedSliders };
                    delete updatedSliders[sliderId];
                }
            }
        }

        this.dispatchTimelineTile({
            type: TIMELINE_TILE_ACTIONS.SET_SLIDERS,
            payload: updatedSliders,
            updateStorePlayhead: false,
        });
    }

    /**
     * function to select and drag sliders
     * @param {object} params
     * @param {React.PointerEvent | PointerEvent | React.MouseEvent | MouseEvent} params.event pointer down or mouse click event
     * @param {string | undefined} params.thumbId id of thumb to drag. should not be passed if whole slider is to be dragged
     * @param {string} params.sliderId id of slider to drag. should be passed even if a specific thumb is to be dragged
     * @param {boolean | undefined} params.canInitiateDrag whether drag can be started on selected slider. defaults to true.
     */
    selectSlider(params = {}) {
        const { event, thumbId, sliderId, canInitiateDrag = true } = params;

        const { initiateSlidersDrag, timelineMode } = this.props;
        const { selectedSliders, timelineTile } = this.state;
        const { sliders, stepper, timelinePlot, tracks } = timelineTile;

        let activeSliderId = sliderId;
        if (sliders[sliderId] && sliders[sliderId].sliderType === SLIDER_TYPES.REFRAME) {
            const currentSelectedSubId = this.props.selectedSubtitles.get(0);
            if (currentSelectedSubId) {
                const currentReframe = this.props.project.getIn(["localAiReframe", currentSelectedSubId]);
                const clickedReframe = this.props.project.getIn(["localAiReframe", sliderId]);
                const isSameTimeframe = currentReframe && clickedReframe &&
                    currentReframe.get("enterStart") === clickedReframe.get("enterStart") &&
                    currentReframe.get("exitEnd") === clickedReframe.get("exitEnd");
                if (isSameTimeframe) {
                    activeSliderId = currentSelectedSubId;
                }
            }
        }

        const selected = selectedSliders.find((s) => s.sliderId === activeSliderId);
        const selectMultiple = event.ctrlKey || event.metaKey;
        let initiateDrag = false;
        let updatedSliders = sliders;
        const tileUpdate = {};

        if (event.type === "pointerdown") {
            this.sliderMoved = false;  // No need to update sliderMoved state here, as it will be updated by handleSlidersDragEnd
            this.isDraggingActive = false;
            if (activeSliderId === playheadId) {
                this.setSelectedSliders({ selectedSliders: [{ sliderId: activeSliderId, thumbId }] });
                initiateDrag = true;
            } else if (updatedSliders[activeSliderId] && !updatedSliders[activeSliderId].preventSelection) {
                const slider = updatedSliders[activeSliderId];
                if (slider.sliderType === SLIDER_TYPES.REFRAME) {
                    const targetId = slider.id;
                    if (targetId) {
                        this.props.setSelectedItems({ selectedItems: [targetId], selectedSubtitles: [targetId] });
                    }
                }
                const isSliderDraggable = slider.isDraggable;
                /** @type {SelectedSliders} */
                let newSelection = [];
                let canDragThumb = Boolean(thumbId && isSliderDraggable);

                if (selectMultiple || selected) {
                    newSelection = selectedSliders.filter((selection) => {
                        const selectedSlider = updatedSliders[selection.sliderId];
                        return (
                            selectedSlider
                            // to not move playhead along with other sliders
                            && selectedSlider.id !== playheadId
                            // even though it is removed now, this will be added later
                            && selectedSlider.id !== activeSliderId
                            // remove gap slider selection
                            && selectedSlider.sliderType !== SLIDER_TYPES.GAP
                            // to not mix draggable with non-draggable
                            && selectedSlider.isDraggable === isSliderDraggable
                            // selection should have either subtitle or other types, not both
                            && (
                                slider.sliderType === SLIDER_TYPES.SUBTITLE && selectedSlider.sliderType === SLIDER_TYPES.SUBTITLE
                                || slider.sliderType !== SLIDER_TYPES.SUBTITLE && selectedSlider.sliderType !== SLIDER_TYPES.SUBTITLE
                            )
                            && (
                                slider.sliderType === SLIDER_TYPES.SMART_FOCUS && selectedSlider.sliderType === SLIDER_TYPES.SMART_FOCUS
                                || slider.sliderType !== SLIDER_TYPES.SMART_FOCUS && selectedSlider.sliderType !== SLIDER_TYPES.SMART_FOCUS
                            )
                            && (
                                slider.sliderType === SLIDER_TYPES.REFRAME && selectedSlider.sliderType === SLIDER_TYPES.REFRAME
                                || slider.sliderType !== SLIDER_TYPES.REFRAME && selectedSlider.sliderType !== SLIDER_TYPES.REFRAME
                            )
                        );
                    });

                    if (canDragThumb && newSelection.length) {
                        // check if other selected sliders supports thumb to be selected
                        canDragThumb = newSelection.every((selection) => {
                            const selectedSlider = updatedSliders[selection.sliderId];
                            return Boolean(selectedSlider[thumbId]);
                        });
                    }
                }

                if (canDragThumb) {
                    // single/multiple slider thumb selection
                    newSelection = newSelection.map((selection) => {
                        return {
                            sliderId: selection.sliderId,
                            thumbId,
                        };
                    });
                    newSelection.push({ sliderId: activeSliderId, thumbId });
                } else if (selectMultiple || selected) {
                    // multiple slider selection
                    newSelection = newSelection.map((selection) => {
                        return {
                            sliderId: selection.sliderId,
                            thumbId: undefined,
                        };
                    });
                    newSelection.push({ sliderId: activeSliderId });
                } else {
                    // single slider/slider-thumb selection
                    // slider-thumb example scenario:
                    //   previously 2 sliders were selected and thumb of another slider is dragged...
                    //   but one of them does not have required thumbId
                    //   in this case we need to allow thumb drag for 3rd slider, deselecting previous 2 sliders
                    newSelection = [{ sliderId: activeSliderId, thumbId }];
                }

                if (isSliderDraggable && newSelection.length) {
                    initiateDrag = true;
                }
                if (timelineMode === TIMELINE_MODES.SUBTITLE) {
                    const supportedSlider = (
                        slider.sliderType === SLIDER_TYPES.PLAYHEAD_INDICATOR
                        || slider.sliderType === SLIDER_TYPES.PLAYHEAD_THUMB
                        || slider.sliderType === SLIDER_TYPES.RULER
                        || slider.sliderType === SLIDER_TYPES.SUBTITLE
                        || (slider.sliderType === SLIDER_TYPES.GAP && slider.itemType === SLIDER_GAP_TYPES.SUBTITLE)
                    );
                    if (!supportedSlider) {
                        this.toggleSubtitleMode(event);
                    }
                }
                if (timelineMode === TIMELINE_MODES.SMART_FOCUS) {
                    const supportedSlider = (
                        slider.sliderType === SLIDER_TYPES.PLAYHEAD_INDICATOR
                        || slider.sliderType === SLIDER_TYPES.PLAYHEAD_THUMB
                        || slider.sliderType === SLIDER_TYPES.RULER
                        || slider.sliderType === SLIDER_TYPES.SMART_FOCUS
                        || (slider.sliderType === SLIDER_TYPES.GAP && slider.itemType === SLIDER_GAP_TYPES.SMART_FOCUS)
                    );
                    if (!supportedSlider) {
                        const skipPanelUpdate = slider.sliderType === SLIDER_TYPES.MINI_REFRAME || slider.sliderType === SLIDER_TYPES.REFRAME;
                        this.toggleSmartFocusMode(event, TIMELINE_MODES.MAIN, skipPanelUpdate);
                    }
                }
                if (timelineMode === TIMELINE_MODES.REFRAME) {
                    const supportedSlider = (
                        slider.sliderType === SLIDER_TYPES.PLAYHEAD_INDICATOR
                        || slider.sliderType === SLIDER_TYPES.PLAYHEAD_THUMB
                        || slider.sliderType === SLIDER_TYPES.RULER
                        || slider.sliderType === SLIDER_TYPES.REFRAME
                        || slider.sliderType === SLIDER_TYPES.SUBTITLE
                        || slider.sliderType === SLIDER_TYPES.MINI_SUBTITLE
                        || (slider.sliderType === SLIDER_TYPES.GAP && slider.itemType === SLIDER_GAP_TYPES.REFRAME)
                    );
                    if (!supportedSlider) {
                        this.toggleAiReframeMode(event)
                    }
                }
                this.setSelectedSliders({ selectedSliders: newSelection });
                this.preventSliderDeselect = !selected; // this is to make sure selected slider doesn't deselect on mouse up (click)
            }

            for (const currentSliderId of Reflect.ownKeys(updatedSliders)) {
                const currentSlider = updatedSliders[currentSliderId];
                if (currentSlider.id !== activeSliderId && currentSlider.sliderType === SLIDER_TYPES.GAP) {
                    // remove all deselected gap sliders
                    updatedSliders = { ...updatedSliders };
                    delete updatedSliders[currentSlider.id];
                }
            }
        } else if (event.type === "click") {
            let canMovePlayhead = true;
            let preventSliderSelection = false;

            if (
                !selectMultiple &&
                updatedSliders[activeSliderId] &&
                updatedSliders[activeSliderId].sliderType === SLIDER_TYPES.MINI_SUBTITLE
            ) {
                const slider = updatedSliders[activeSliderId];
                event.stopPropagation();
                canMovePlayhead = false;
                this.toggleSubtitleMode(event);
                if (slider.meta && slider.meta.firstSubtitleId) {
                    // select subtitle using redux instead of timeline, as no subtitle sliders will be in internal state at this point
                    this.props.setSelectedItems({ selectedSubtitles: [slider.meta.firstSubtitleId.timelineId], type: "SUBTITLE" });
                    preventSliderSelection = true;
                }
            } else if (
                !selectMultiple &&
                updatedSliders[activeSliderId] &&
                updatedSliders[activeSliderId].sliderType === SLIDER_TYPES.MINI_SMART_FOCUS
            ) {
                const slider = updatedSliders[activeSliderId];
                event.stopPropagation();
                canMovePlayhead = false;
                this.toggleSmartFocusMode(event);
                if (slider.meta && slider.meta.firstSubtitleId) {
                    // select subtitle using redux instead of timeline, as no subtitle sliders will be in internal state at this point
                    this.props.setSelectedItems({ selectedSubtitles: [slider.meta.firstSubtitleId.timelineId], propWindowType: "SMART_FOCUS" });
                    preventSliderSelection = true;
                }
            }
            else if (
                !selectMultiple &&
                updatedSliders[activeSliderId] &&
                updatedSliders[activeSliderId].sliderType === SLIDER_TYPES.MINI_REFRAME
            ) {
                const slider = updatedSliders[activeSliderId];
                event.stopPropagation();
                const targetId = slider.meta?.targetId;
                if (targetId) {
                    this.props.setSelectedItems({ selectedItems: [targetId], type: "REFRAME" });
                }
                this.setSelectedSliders({ selectedSliders: [{ sliderId: activeSliderId }] });

                // Move playhead to click position
                updatedSliders = this.placePlayheadOnClick(event, null, updatedSliders);
                const playheadThumb = updatedSliders[playheadId]?.enterStart;
                const clickTime = playheadThumb ? (playheadThumb.position.step * this.state.timelineTile.stepper.interval) : undefined;
                this.toggleAiReframeMode(event, TIMELINE_MODES.REFRAME, targetId, clickTime);
                preventSliderSelection = true;
                canMovePlayhead = false;
            }
            if (!preventSliderSelection && !this.preventSliderDeselect && selected && !this.sliderMoved && selectedSliders.length > 1) {
                if (selectMultiple) {
                    const newSelection = selectedSliders.filter((s) => s.sliderId !== activeSliderId);
                    this.setSelectedSliders({ selectedSliders: newSelection });
                } else {
                    this.setSelectedSliders({ selectedSliders: [{ sliderId: activeSliderId }] });
                }
            }
            // Avoid playhead placement on drag and multiple selection.
            if (canMovePlayhead && !this.sliderMoved && !selectMultiple) {
                // Placing the playhead on click position.
                updatedSliders = this.placePlayheadOnClick(event, null, updatedSliders);
            }
            this.preventSliderDeselect = false;
        }

        if (initiateDrag && canInitiateDrag) {
            this.sliderMoved = false;
            this.setState({ sliderMoved: false });
            this.isDraggingActive = false;
            updatedSliders = this.hidePlayheadIndication({ updatedSliders });
            this.setState({ timelineAutoScrollData: null });
            initiateSlidersDrag({
                event,
                throttle: (1 / 90) * 1000,
                onMouseDrag: this.handleSlidersDrag,
                onMouseDragEnd: this.handleSlidersDragEnd,
                clientOffset: { x: -timelinePlot.x, y: -(timelinePlot.y + tracksOffsetY) },
                scrollLeftElRef: this.timelineInnerRef,
                scrollTopElRef: this.trackListContainerRef,
                cursor: thumbId ? "ew-resize" : "grabbing",
                cursorThreshold: this.dragStartThreshold,
            });
        }

        if (stepper.videoExcessSteps) {
            const updatedStepper = { ...stepper, videoExcessSteps: 0 };
            tileUpdate.stepper = updatedStepper;
            tileUpdate.tracks = updateTrackWidth({ stepper: updatedStepper, tracks });
        }

        tileUpdate.sliders = updatedSliders;
        this.dispatchTimelineTile({ type: TIMELINE_TILE_ACTIONS.MERGE_STATE, payload: tileUpdate });
    }

    /**
     * function to remove selected gap sliders
     * @param {object} params
     * @param {Sliders} params.updatedSliders can be passed if this func is called along with other slider update functions
     * @param {SelectedSliders} params.updatedSelectedSliders can be passed if this func is called along with other selection update functions
     * @param {boolean | undefined} params.removeAll whether all gap sliders are to be removed instead of selected gap sliders
     */
    removeGapSlider(params = {}) {
        const { removeAll = false } = params;
        let { updatedSliders, updatedSelectedSliders } = params;

        const { selectedSliders, timelineTile } = this.state;
        const { sliders } = timelineTile;

        if (!updatedSliders) {
            updatedSliders = sliders;
        }
        if (!updatedSelectedSliders) {
            updatedSelectedSliders = selectedSliders;
        }

        if (removeAll) {
            for (const sliderId of Reflect.ownKeys(updatedSliders)) {
                const currentSlider = updatedSliders[sliderId];
                if (currentSlider.sliderType === SLIDER_TYPES.GAP) {
                    updatedSliders = { ...updatedSliders };
                    delete updatedSliders[currentSlider.id];
                }
            }
        } else {
            updatedSelectedSliders.forEach((selection) => {
                const currentSlider = updatedSliders[selection.sliderId];
                if (currentSlider.sliderType === SLIDER_TYPES.GAP) {
                    updatedSliders = { ...updatedSliders };
                    delete updatedSliders[currentSlider.id];
                }
            });
        }
        updatedSelectedSliders = updatedSelectedSliders.filter((selection) => updatedSliders[selection.sliderId]); // to remove gap selection

        this.dispatchTimelineTile({ type: TIMELINE_TILE_ACTIONS.SET_SLIDERS, payload: updatedSliders });
        this.setSelectedSliders({ selectedSliders: updatedSelectedSliders });

        return {
            updatedSliders,
            updatedSelectedSliders,
        };
    }

    /**
     * @param {number} playhead 
     */
    placePlayheadAtTime(seconds) {
        const { timelineTile } = this.state;
        const { stepper } = timelineTile;
        const step = secondsToStep({ seconds, stepper });
        this.placePlayheadOnClick(null, { x: step.x, y: 0 });
    }

    /**
     * handle playhead thumb position on mouse click
     * @param {PointerEvent | React.PointerEvent | null} event
     * @param {{ x: number, y: number } | undefined} moveTo
     */
    placePlayheadOnClick(event, moveTo, updatedSliders) {
        const { isPlayAll } = this.props;
        const { timelineTile } = this.state;
        const { sliders, stepper, timelineSnapPoints, timelinePlot } = timelineTile;
        if (!updatedSliders) {
            updatedSliders = sliders;
        }

        if (!moveTo) {
            const mousePosition = getMouseClientPosition(event);
            let scrollLeft = 0;
            if (this.timelineInnerRef.current instanceof HTMLElement) {
                scrollLeft = this.timelineInnerRef.current.scrollLeft;
            }
            moveTo = { x: scrollLeft + mousePosition.x - timelinePlot.x, y: 0 };
        }

        const playheadIndicator = updatedSliders[indicatorId];
        const playheadThumb = updatedSliders[playheadId].enterStart;
        const moveSlidersResult = moveSliders({
            moveTo,
            startedFrom: { x: playheadThumb.position.x },
            moveType: isPlayAll ? "drag" : "place",
            scrollType: "not-scrolling",
            selectedSliders: [{ sliderId: playheadThumb.sliderId, thumbId: playheadThumb.id }], // playhead will not be selected on ruler click, so make it up
            sliders: updatedSliders,
            stepper,
        });
        const { matchingSnapPoints } = moveSlidersResult;
        updatedSliders = moveSlidersResult.sliders;
        if (playheadIndicator.isVisible) {
            updatedSliders = {
                // sliders
                ...updatedSliders,
                [indicatorId]: {
                    // slider
                    ...updatedSliders[indicatorId],
                    isVisible: false,
                },
            };
        }
        const updatedTimelineSnapPoints = removeDefaultSnapPoints({ timelineSnapPoints });
        addDefaultSnapPoints({
            mutableTimelineSnapPoints: updatedTimelineSnapPoints, // it is okay to use mutation logic here as we have removed whole trace of default sliders
            sliders: updatedSliders,
        });
        this.dispatchTimelineTile({
            type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
            payload: {
                sliders: updatedSliders,
                timelineSnapPoints: updatedTimelineSnapPoints,
                matchingSnapPoints,
            },
        });

        const newPlayheadThumb = updatedSliders[playheadId].enterStart;
        const willMovePlayhead = playheadThumb.position.step !== newPlayheadThumb.position.step;
        if (willMovePlayhead && !isPlayAll) {
            this.setState({ animatePlayhead: true });
        }

        return updatedSliders;
    }

    /**
     * to increase/decrease playhead step on click
     * @param {number} step
     */
    adjustPlayheadStepBy(step) {
        const { timelineTile } = this.state;
        const { sliders, stepper } = timelineTile;

        const stepOffset = step * stepper.stepSizePx;
        const playheadPosition = getTrackPosition({ slider: sliders[playheadId], thumbIds: ["enterStart"] }).enterStart;
        this.placePlayheadOnClick(null, { x: playheadPosition.x + stepOffset, y: 0 });
    }

    /**
     * Function to set multiselect flag and update other related stuff based on flag
     * @param {object} params
     * @param {PointerEvent | React.PointerEvent} params.event
     * @param {boolean} params.isSelecting
     * @param {SelectedSliders | undefined} params.selectionToSet
     */
    updateIsSliderMultiSelect(params = {}) {
        const { event, isSelecting, isGapSlider, isContextClick } = params;
        let { selectionToSet } = params;
        const { selectedSliders, timelineTile } = this.state;
        const { sliders } = timelineTile;

        const prevIsSliderMultiSelect = this.isSliderMultiSelect;
        this.setState({ isSliderMultiSelect: isSelecting });
        this.isSliderMultiSelect = isSelecting;

        if (isSelecting) {
            const { updatedSliders, updatedSelectedSliders } = this.removeGapSlider({
                updatedSelectedSliders: selectionToSet,
                removeAll: true,
            });
            selectionToSet = updatedSelectedSliders;
            this.hidePlayheadIndication({ updatedSliders });
        } else if (!isContextClick) {
            let updatedSliders = sliders;
            if (!prevIsSliderMultiSelect && !isGapSlider && event.type === "pointerup") {
                updatedSliders = this.placePlayheadOnClick(event);
            } else {
                updatedSliders = this.movePlayheadIndication(event);
            }

            if (
                selectionToSet &&
                selectionToSet.length === 0 &&
                selectedSliders.length > 0
            ) {
                // to remove gap sliders when clicked on track
                const { updatedSelectedSliders } = this.removeGapSlider({
                    updatedSliders,
                    updatedSelectedSliders: selectionToSet,
                    removeAll: true,
                });
                selectionToSet = updatedSelectedSliders;
            }
        }

        if (selectionToSet) {
            const updatedMultiItems = selectionToSet.filter((itemId) => {
                return !this.props.project.getIn([
                    "workspaceItems",
                    itemId.sliderId,
                    "isLock",
                ])
            })
            this.setSelectedSliders({ selectedSliders: updatedMultiItems });
        }
    }

    handleGapRemove({ deleteType = "magnet" } = {}) {
        const { project } = this.props;
        const { selectedSliders, timelineTile } = this.state;
        const { sliders, stepper } = timelineTile;

        const { updatedSliderIds, updatedSliders } = removeSliderGaps({
            selectedSliders,
            sliders,
            deleteType,
        });

        const toUpdate = getUpdatedTimeline({
            project,
            selectedSliders: updatedSliderIds,
            sliders: updatedSliders,
            stepper,
        });

        this.dispatchTimelineTile({ type: TIMELINE_TILE_ACTIONS.SET_SLIDERS, payload: updatedSliders });
        if (toUpdate.length) {
            this.props.updateTimelineTime({ toUpdate });
        }
    }

    /**
     * @param {object} params
     * @param {"left" | "right"} params.direction
     * @param {"step" | "nearest-thumb"} params.seekButtonBehaviour
     */
    onSeekClick(params = {}) {
        const { direction = "right", seekButtonBehaviour = RULER_OPTIONS.seekButtonBehaviour } = params;
        const { timelineMode, } = this.props;
        const { selectedSliders, timelineTile } = this.state;
        const { sliders, stepper, timelineSnapPoints } = timelineTile;

        if (seekButtonBehaviour === "step") {
            const step = direction === "left" ? -1 : 1;
            this.adjustPlayheadStepBy(step);
        } else if (seekButtonBehaviour === "nearest-thumb") {
            const result = movePlayheadToNearestThumb({
                direction,
                selectedSliders,
                sliders,
                stepper,
                timelineSnapPoints,
                timelineMode,
            });
            if (result.moveSlidersResult && result.timelineSnapPointsResult) {
                this.dispatchTimelineTile(
                    {
                        type: TIMELINE_TILE_ACTIONS.MERGE_STATE,
                        payload: {
                            sliders: result.moveSlidersResult.sliders,
                            matchingSnapPoints: result.moveSlidersResult.matchingSnapPoints,
                            timelineSnapPoints: result.timelineSnapPointsResult,
                        },
                    },
                    () => {
                        this.autoScrollPlayhead({
                            positionToUse: "position",
                            autoscrollType: "nearest-thumb",
                        });
                    }
                );
            }
            if (result.selectedSlidersResult) {
                this.setSelectedSliders({
                    selectedSliders: result.selectedSlidersResult,
                });
            }
        }
    }

    onContainerTransitionEnd(event) {
        if (event.target === event.currentTarget) {
            this.setState({ animateTimelineContainer: false });
        }
    }

    /**
     * @param {ScrollToOptions} scrollOptions 
     */
    rulerScrollBy(scrollOptions) {
        if (this.timelineInnerRef.current) {
            this.timelineInnerRef.current.scrollBy(scrollOptions);
        }
    }

    splitHandler() {
        const { selectedSliders, timelineTile } = this.state;
        const { project, timelineMode, workspaceWidth, zoomFactor, cleanAudio } = this.props;
        const { sliders, timelinePlot, tracks, stepper } = timelineTile;
        const selectedToSplit = [];
        const thumb = sliders[playheadId].enterStart;
        let { position } = thumb;
        if (thumb.trackPosition) {
            position = thumb.trackPosition;
        }
        const { step } = position;
        const splitTime = step * stepper.interval;

        const { OBJECT, AUDIO, VIDEO, REFRAME } = SLIDER_TYPES;

        selectedSliders.forEach(({ sliderId }) => {

            const { sliderType, id, enterStart, exitEnd } = sliders[sliderId] || {};
            const isTimelineSlider =
                sliderType === OBJECT || sliderType === AUDIO || sliderType === VIDEO;

            // SUBTITLE check
            const subtitleCondition =
                timelineMode === TIMELINE_MODES.SUBTITLE &&
                sliderType === SLIDER_TYPES.SUBTITLE &&
                enterStart.position.step < step &&
                exitEnd.position.step > step;

            if (subtitleCondition) {
                const slider = sliders[sliderId];
                selectedToSplit.push({ subtitleId: slider.subtitleId, container: "subtitle" });
            }

            // REFRAME check
            const reframeCondition =
                timelineMode === TIMELINE_MODES.REFRAME &&
                sliderType === REFRAME &&
                enterStart.position.step < step &&
                exitEnd.position.step > step;
            if (reframeCondition) {
                selectedToSplit.push({ id: sliderId, container: "workspaceItems" });
            }

            // MAIN check
            const mainCondition =
                timelineMode === TIMELINE_MODES.MAIN &&
                isTimelineSlider &&
                sliderId === id &&
                enterStart.position.step < step &&
                exitEnd.position.step > step;

            if (mainCondition) {
                const container =
                    sliderType === OBJECT
                        ? "workspaceItems"
                        : sliderType === VIDEO
                            ? "workspaceBG"
                            : "audios";
                selectedToSplit.push({ id: sliderId, container });
            }
        });

        // SUBTITLE / SMART_FOCUS viewport check
        const viewportCondition =
            (timelineMode === TIMELINE_MODES.SUBTITLE ||
                timelineMode === TIMELINE_MODES.SMART_FOCUS ||
                timelineMode === TIMELINE_MODES.REFRAME)
            && !selectedToSplit.length
            && this.timelineInnerRef.current;


        if (viewportCondition) {
            const timelineScrollX = this.timelineInnerRef.current.scrollLeft;

            const findSliderToSplit = (currentSlider) => {
                const { subtitleId, enterStart, exitEnd, sliderType, id } = currentSlider;
                if (enterStart.position.step < step && exitEnd.position.step > step) {
                    if (timelineMode === TIMELINE_MODES.REFRAME && sliderType === SLIDER_TYPES.REFRAME) {
                        selectedToSplit.push({ id, container: "workspaceItems" });
                    } else {
                        selectedToSplit.push({ subtitleId, container: "subtitle" });
                    }
                }
                return true;
            };

            iterViewportSliders({
                callback: findSliderToSplit,
                checkItemIds: false,
                checkSubtitleIds: true,
                sliders,
                viewX: timelineScrollX,
                viewY: 0,
                viewWidth: timelinePlot.width,
                viewHeight: 0,
                tracks,
                tracksToCheck: [TRACK_TYPES.OBJECT, TRACK_TYPES.AUDIO],
                axis: 1,
            });
        }

        const ItemToUpdate = [];
        const newSubtitleDropIds = new Set();
        const globalTextStyles = project.getIn(["subtitle", "textStyles"]);
        const globalTextEffects = project.getIn(["subtitle", "textEffects"]);


        const processSplitItem = (item, container) => {
            const { currentItem, newItem: newItemResult } = splitSliderHelper({
                item,
                splitTime,
                container,
                projectWidth: project.get("width"),
                projectHeight: project.get("height"),
            });

            let newItem = newItemResult;
            if ((newItem.get("isReframe") || isVideoOnly(newItem.get("type"), newItem.get("subType")) || container === "audios") && !newItem.has("sceneId")) {
                newItem = newItem.set("dropId", newItem.get("id"));
                if (item.get("isReframe")) {
                    const origReframeId = item.get("reframeOriginalId") || item.get("id");
                    newItem = newItem.set("reframeOriginalId", origReframeId);
                }
            }

            const currentItemUpdate = { container, id: currentItem.get("id"), toUpdate: currentItem.toJS(), isSplit: true };
            const newItemUpdate = { container, id: newItem.get("id"), newItemData: newItem.toJS(), isAdd: true, isSplit: true };

            ItemToUpdate.push(currentItemUpdate, newItemUpdate);

            // Subtitle split/move logic when video/audio has subtitles and dropId changes
            if (project.getIn(["subtitle", "data", item.get("dropId")])) {
                const oldDropId = item.get("dropId");
                const newDropId = newItem.get("dropId");

                if (newDropId !== oldDropId) {
                    if (!project.getIn(["subtitle", "data", newDropId]) && !newSubtitleDropIds.has(newDropId)) {
                        ItemToUpdate.push({
                            container: "subtitleDropData",
                            dropId: newDropId,
                            isAdd: true,
                            newItemData: {},
                        });
                        newSubtitleDropIds.add(newDropId);
                    }
                    const subtitleMap = project.getIn(["subtitle", "data", oldDropId]);
                    const speed = Number.isFinite(item.get("speed")) ? item.get("speed") : 1;
                    const mediaStart = (container === "workspaceItems" || container === "workspaceBG") ? item.get("videoStart") : item.get("musicStart");
                    const itemStart = container === "workspaceItems" ? item.get("enterStart") : item.get("playStart");

                    subtitleMap.forEach((subtitleItem, subtitleItemId) => {
                        const subTimelineStart = itemStart + (subtitleItem.get("start") - mediaStart) / speed;
                        const subTimelineEnd = itemStart + (subtitleItem.get("end") - mediaStart) / speed;

                        if (subTimelineEnd <= splitTime) {
                            // Case 1: Subtitle is completely before the split point.
                            // Keep it under oldDropId (no action needed).
                        } else if (subTimelineStart >= splitTime) {
                            // Case 2: Subtitle is completely after the split point.
                            // Move it to newDropId: delete from oldDropId and add to newDropId.
                            ItemToUpdate.push({
                                container: "subtitleData",
                                id: subtitleItemId,
                                dropId: oldDropId,
                                timelineId: `${item.get("id")}-${oldDropId}-${subtitleItemId}`,
                                isDelete: true,
                            });
                            ItemToUpdate.push({
                                container: "subtitleData",
                                id: subtitleItemId,
                                dropId: newDropId,
                                langId: project.get("defaultSubtitle"),
                                timelineId: `${newItem.get("id")}-${newDropId}-${subtitleItemId}`,
                                isAdd: true,
                                isSplit: true,
                                newItemData: subtitleItem.toJS(),
                            });
                        } else {
                            // Case 3: Subtitle spans across the split point.
                            // Split it into two separate subtitle items using the splitSubtitle utility.
                            const subtitleIdObj = {
                                itemId: item.get("id"),
                                dropId: oldDropId,
                                itemContainer: container,
                                langId: project.get("defaultSubtitle"),
                            };
                            const splitResult = splitSubtitle({
                                splitTime,
                                subtitleItem,
                                subtitleId: subtitleIdObj,
                                targetItem: item,
                                workspaceWidth,
                                zoomFactor,
                                globalTextStyles,
                                globalTextEffects,
                            });
                            if (splitResult.left && splitResult.right) {
                                // Delete the original unsplit subtitle
                                ItemToUpdate.push({
                                    container: "subtitleData",
                                    id: subtitleItemId,
                                    dropId: oldDropId,
                                    timelineId: `${item.get("id")}-${oldDropId}-${subtitleItemId}`,
                                    isDelete: true,
                                });
                                // Add left split subtitle to oldDropId
                                ItemToUpdate.push({
                                    container: "subtitleData",
                                    id: splitResult.left.get("id"),
                                    dropId: oldDropId,
                                    langId: project.get("defaultSubtitle"),
                                    timelineId: `${item.get("id")}-${oldDropId}-${splitResult.left.get("id")}`,
                                    isAdd: true,
                                    isSplit: true,
                                    newItemData: splitResult.left.toJS(),
                                });
                                // Add right split subtitle to newDropId
                                ItemToUpdate.push({
                                    container: "subtitleData",
                                    id: splitResult.right.get("id"),
                                    dropId: newDropId,
                                    langId: project.get("defaultSubtitle"),
                                    timelineId: `${newItem.get("id")}-${newDropId}-${splitResult.right.get("id")}`,
                                    isAdd: true,
                                    isSplit: true,
                                    newItemData: splitResult.right.toJS(),
                                });
                            }
                        }
                    });
                }
            }

            if (isReframeDropItem(item)) {
                const currentItemExitEnd = currentItem.get("exitEnd");
                const newItemExitEnd = newItem.get("exitEnd");
                const originalItemId = item.get("id");
                const newItemId = newItem.get("id");

                let originalTransitionEffect = "none_transition";
                const originalScenes = [];
                project.get("workspaceItems").forEach((sceneItem) => {
                    if (sceneItem?.get("originalId") === originalItemId && !!sceneItem?.get("sceneData")) {
                        originalScenes.push(sceneItem);
                    }
                });
                if (originalScenes.length > 0) {
                    originalScenes.sort((a, b) => (a.get("enterStart") || 0) - (b.get("enterStart") || 0));
                    const lastOrigScene = originalScenes[originalScenes.length - 1];
                    const enterEff = lastOrigScene.get("transitionEnterEffect");
                    if (enterEff && enterEff !== "none_transition") {
                        originalTransitionEffect = enterEff;
                    }
                }

                const leftBoundarySceneIds = [];
                const rightBoundarySceneIds = [];

                project.get("workspaceItems").forEach((sceneItem, sceneItemId) => {
                    if (sceneItem?.get("originalId") === originalItemId && !!sceneItem?.get("sceneData")) {
                        const sceneEnterStart = sceneItem.get("enterStart");
                        const sceneExitEnd = sceneItem.get("exitEnd");
                        const sceneData = sceneItem.get("sceneData")?.toJS ? sceneItem.get("sceneData").toJS() : sceneItem.get("sceneData");

                        if (sceneEnterStart >= currentItemExitEnd && sceneExitEnd <= newItemExitEnd) {
                            ItemToUpdate.push({
                                container: "workspaceItems",
                                id: sceneItemId,
                                toUpdate: { originalId: newItemId, dropId: newItemId },
                            });
                            if (sceneEnterStart === currentItemExitEnd) {
                                rightBoundarySceneIds.push(sceneItemId);
                            }
                        } else if (sceneEnterStart < currentItemExitEnd && sceneExitEnd > currentItemExitEnd) {
                            const sceneItemSpeed = Number.isFinite(sceneItem.get("speed")) ? sceneItem.get("speed") : 1;
                            const sceneCurrentItemDuration = currentItemExitEnd - sceneEnterStart;
                            const sceneCurrentEndDuration = ((sceneItem.get("videoStart") || 0) / sceneItemSpeed) + sceneCurrentItemDuration;
                            const splitVideoStart = sceneCurrentEndDuration * sceneItemSpeed;

                            ItemToUpdate.push({
                                container: "workspaceItems",
                                id: sceneItemId,
                                toUpdate: {
                                    exitEnd: currentItemExitEnd,
                                    exitStart: currentItemExitEnd,
                                    videoEnd: splitVideoStart
                                },
                            });
                            leftBoundarySceneIds.push(sceneItemId);

                            const newSceneId = `scene${sceneData?.scene_id}-${randomString("split")}`;
                            const newSceneItem = sceneItem.toJS();
                            ItemToUpdate.push({
                                container: "workspaceItems",
                                id: newSceneId,
                                newItemData: {
                                    ...newSceneItem,
                                    id: newSceneId,
                                    enterStart: currentItemExitEnd,
                                    enterEnd: currentItemExitEnd,
                                    exitStart: sceneExitEnd,
                                    exitEnd: sceneExitEnd,
                                    videoStart: splitVideoStart,
                                    originalId: newItemId,
                                    dropId: newItemId,
                                    track: newSceneItem.track,
                                    isDontSelect: true,
                                },
                                isAdd: true,
                                isSplit: true,
                            });
                            rightBoundarySceneIds.push(newSceneId);
                        } else if (sceneExitEnd === currentItemExitEnd) {
                            leftBoundarySceneIds.push(sceneItemId);
                        }
                    }
                });

                if (originalTransitionEffect !== "none_transition" && leftBoundarySceneIds.length > 0 && rightBoundarySceneIds.length > 0) {
                    const updateSceneTransition = (sceneId, transitionProps) => {
                        const existing = ItemToUpdate.find(u => u.container === "workspaceItems" && u.id === sceneId);
                        if (existing) {
                            if (existing.toUpdate) {
                                existing.toUpdate = { ...existing.toUpdate, ...transitionProps };
                            } else if (existing.newItemData) {
                                existing.newItemData = { ...existing.newItemData, ...transitionProps };
                            }
                        } else {
                            ItemToUpdate.push({
                                container: "workspaceItems",
                                id: sceneId,
                                toUpdate: transitionProps
                            });
                        }
                    };

                    leftBoundarySceneIds.forEach(lId => {
                        updateSceneTransition(lId, {
                            transitionEnterEffect: originalTransitionEffect,
                            transitionEnterId: rightBoundarySceneIds[0],
                            transitionDuration: transitionTime
                        });
                    });

                    rightBoundarySceneIds.forEach(rId => {
                        updateSceneTransition(rId, {
                            transitionExitEffect: originalTransitionEffect,
                            isTransition: true,
                            transitionExitId: leftBoundarySceneIds[0],
                            transitionDuration: transitionTime
                        });
                    });
                }
            }
        };

        if (selectedToSplit.length) {

            selectedToSplit.forEach((selectedItem) => {

                if (selectedItem.container === "subtitle") {
                    const { subtitleId } = selectedItem;
                    const subtitleItem = project.getIn(["subtitle", "data", subtitleId.dropId, subtitleId.id]);
                    const targetItem = project.getIn([subtitleId.itemContainer, subtitleId.itemId]);


                    if (subtitleItem && targetItem) {
                        const splitResult = splitSubtitle({
                            splitTime, subtitleItem, subtitleId, targetItem,
                            workspaceWidth, zoomFactor, globalTextStyles, globalTextEffects
                        });
                        if (splitResult.left && splitResult.right) {
                            ItemToUpdate.push(
                                {
                                    container: "subtitleData",
                                    id: splitResult.left.get("id"),
                                    dropId: subtitleId.dropId,
                                    langId: subtitleId.langId,
                                    timelineId: `${subtitleId.itemId}-${subtitleId.dropId}-${splitResult.left.get("id")}`,
                                    toUpdate: splitResult.left.toJS(),
                                },
                                {
                                    container: "subtitleData",
                                    id: splitResult.right.get("id"),
                                    dropId: subtitleId.dropId,
                                    langId: subtitleId.langId,
                                    timelineId: `${subtitleId.itemId}-${subtitleId.dropId}-${splitResult.right.get("id")}`,
                                    isAdd: true,
                                    isSplit: true,
                                    newItemData: splitResult.right.toJS(),
                                }
                            );
                        }
                    }
                } else if (timelineMode === TIMELINE_MODES.REFRAME) {
                    const wItems = project.getIn(["workspaceItems"]);
                    let targetSceneId = null;
                    let targetItem = wItems.get(selectedItem.id);

                    if (!targetItem) {
                        wItems.forEach((workspaceItem) => {
                            if (workspaceItem.get("sceneId") === selectedItem.id) {
                                targetItem = workspaceItem;
                            }
                        });
                    }

                    if (targetItem) {
                        targetSceneId = targetItem.get("sceneId");
                    }

                    if (targetSceneId) {
                        const siblings = [];
                        wItems.forEach((workspaceItem) => {
                            if (workspaceItem.get("sceneId") === targetSceneId) {
                                siblings.push(workspaceItem);
                            }
                        });

                        if (siblings.length === 0) {
                            console.error("❌ [SPLIT] No siblings found for targetSceneId:", targetSceneId);
                        } else {
                            siblings.forEach((siblingItem) => {
                                if (cleanAudio.get("id") !== siblingItem.get("id")) {
                                    processSplitItem(siblingItem, "workspaceItems");
                                }
                            });
                        }
                    } else {
                        console.error("❌ [SPLIT] targetItem / targetSceneId not found for selectedItem.id:", selectedItem.id);
                    }
                } else {
                    const item = project.getIn([selectedItem.container, selectedItem.id]);
                    if (
                        item?.get("id") === selectedItem.id &&
                        cleanAudio.get("id") !== item.get("id")
                    ) {
                        processSplitItem(item, selectedItem.container);
                    } else {
                        console.error("❌ [SPLIT] Item failed guard check — NOT processing split");
                    }
                }
            });
        } else if (timelineMode === TIMELINE_MODES.MAIN) {

            project.get("workspaceItems").forEach((item) => {
                if (item.get("originalId") || item.get("sceneData")) {
                    return;
                }

                if (
                    item.get("enterStart") < splitTime &&
                    item.get("exitEnd") > splitTime &&
                    !item.get("isLock") &&
                    cleanAudio.get("id") !== item.get("id")
                ) {
                    processSplitItem(item, "workspaceItems");
                }
            });

            ["audios", "workspaceBG"].forEach((container) => {
                project.get(container).forEach((item) => {
                    if (
                        item.get("playStart") < splitTime &&
                        item.get("playEnd") > splitTime &&
                        cleanAudio.get("id") !== item.get("id")
                    ) {
                        processSplitItem(item, container);
                    }
                });
            });
        } else if (timelineMode === TIMELINE_MODES.REFRAME) {
            project.get("workspaceItems").forEach((item) => {
                if (!item.get("sceneData")) {
                    return;
                }

                if (
                    item.get("enterStart") < splitTime &&
                    item.get("exitEnd") > splitTime &&
                    !item.get("isLock") &&
                    cleanAudio.get("id") !== item.get("id")
                ) {
                    processSplitItem(item, "workspaceItems");
                }
            });
        } else {
            console.error("❌ [SPLIT] Fell into else — no branch matched");
        }

        if (ItemToUpdate.length) {
            this.props.updateTimelineTime({ toUpdate: ItemToUpdate });
        } else {
            console.error("❌ [SPLIT] ItemToUpdate is EMPTY — nothing was dispatched");
        }
    }

    toggleSubtitleMode(e, switchTo, skipPanelUpdate = false) {
        const { timelineMode } = this.props;
        if (!Number.isFinite(switchTo)) {
            switchTo = timelineMode === TIMELINE_MODES.SUBTITLE ? TIMELINE_MODES.MAIN : TIMELINE_MODES.SUBTITLE;
        }


        // Reset drag state when toggling modes to prevent stale isDragging state
        this.handleSlidersDragEnd({});

        if (switchTo === TIMELINE_MODES.SUBTITLE) {
            this.props.setLibraryPanel(PANEL.SUBTITLE, true);
        } else {
            // Determine the appropriate panel based on the selected item's type
            const selectedWorkspaceItem = this.props.selectedItems;
            const { selectedSubtitles, project } = this.props;
            const selectedItemId = selectedSubtitles?.size > 0 ?
                getParentItemIdFromSubtitleId(project, selectedSubtitles?.get(0)) : selectedWorkspaceItem?.get(0);
            const selectedItem = selectedItemId ? project.getIn(["workspaceItems", selectedItemId]) : null;

            let panelToOpen = PANEL.TEXT; // Default fallback
            if (selectedItem) {
                const itemType = selectedItem.get("type");
                const itemSubType = selectedItem.get("subType");
                if (itemType === "TEXT") {
                    panelToOpen = PANEL.TEXT;
                } else if (isImageOnly(itemType, itemSubType) || isVideoOnly(itemType, itemSubType)) {
                    panelToOpen = PANEL.PROPERTIES;
                }
            }
            if (!skipPanelUpdate) {
                this.props.setLibraryPanel(panelToOpen, undefined, { retainExpand: true });
            }
        }
    }

    toggleSmartFocusMode(e, switchTo, skipPanelUpdate = false) {
        const { timelineMode, project } = this.props;
        if (!Number.isFinite(switchTo)) {
            switchTo = timelineMode === TIMELINE_MODES.SMART_FOCUS ? TIMELINE_MODES.MAIN : TIMELINE_MODES.SMART_FOCUS;
        }
        if (switchTo === TIMELINE_MODES.SMART_FOCUS) {
            if (this.props.isMobileView) {
                this.props.setPropertyWindow({ component: content.SMART_FOCUS_MOBILE, isOpened: false });
            } else {
                this.props.setPropertyWindow({ component: content.SMART_FOCUS });
            }
        } else {
            // Determine the appropriate panel based on the selected item's type
            const selectedWorkspaceItem = this.props.selectedItems;
            const { selectedSubtitles } = this.props;
            const selectedItemId = selectedSubtitles?.size > 0
                ? getParentItemIdFromSubtitleId(project, selectedSubtitles?.get(0)) : selectedWorkspaceItem?.get(0);
            const selectedItem = selectedItemId ? project.getIn(["workspaceItems", selectedItemId]) : null;

            let panelToOpen = PANEL.TEXT; // Default fallback
            if (selectedItem) {
                const itemType = selectedItem.get("type");
                const itemSubType = selectedItem.get("subType");
                if (itemType === "TEXT") {
                    panelToOpen = PANEL.TEXT;
                } else if (isImageOnly(itemType, itemSubType) || isVideoOnly(itemType, itemSubType)) {
                    panelToOpen = PANEL.PROPERTIES;
                }
            }
            if (!skipPanelUpdate) {
                this.props.setLibraryPanel(panelToOpen, undefined, { retainExpand: true });
            }
        }
    }

    /**
     * Finds the scene at the given playhead position.
     * At boundaries between scenes, prefers the scene with the latest enterStart (i.e. the starting one).
     * Returns ALL items belonging to that scene (grouped by sceneId).
     */
    _findSceneAtPlayhead(workspaceItems, playhead, parentClipId, EPSILON = 0.001) {
        let bestSceneId = null;
        let bestEnterStart = -Infinity;
        let bestOriginalId = null;

        // Step 1: Find which sceneId the playhead falls into
        workspaceItems.forEach((item) => {
            if (!item.get("sceneData")) return;

            if (parentClipId && item.get("originalId") !== parentClipId && item.get("dropId") !== parentClipId) {
                return;
            }

            const enterStart = item.get("enterStart");
            const exitEnd = item.get("exitEnd");
            const sceneId = item.get("sceneId");

            const isInRange =
                playhead >= enterStart - EPSILON &&
                playhead <= exitEnd + EPSILON;

            if (isInRange) {
                // Among overlapping scenes, prefer the one starting latest (boundary preference)
                if (enterStart > bestEnterStart) {
                    bestEnterStart = enterStart;
                    bestSceneId = sceneId;
                    bestOriginalId = item.get("originalId") || item.get("dropId");
                }
            }
        });


        if (!bestSceneId) return null;

        // Step 2: Collect ALL items that belong to the winning sceneId AND parent clip
        const dropIds = [];
        const itemIds = [];

        workspaceItems.forEach((item, itemId) => {
            if (item.get("sceneId") === bestSceneId && (item.get("originalId") === bestOriginalId || item.get("dropId") === bestOriginalId)) {
                dropIds.push(item.get("dropId"));
                itemIds.push(itemId);
            }
        });

        return {
            dropIds,   // all dropIds for this scene (one per person/layout slot)
            itemIds,   // all item keys for this scene
            sceneId: bestSceneId,
        };
    }

    toggleAiReframeMode(e, switchTo, targetItemId, playheadTime) {
        const { timelineMode, project, playhead: propsPlayhead } = this.props;
        const actualPlayhead = playheadTime !== undefined ? playheadTime : propsPlayhead;
        const isSubtitleMode = timelineMode === TIMELINE_MODES.SUBTITLE;

        // Reset drag state when toggling modes to prevent stale isDragging state
        this.handleSlidersDragEnd({});

        if (!Number.isFinite(switchTo)) {
            switchTo = timelineMode === TIMELINE_MODES.REFRAME ? TIMELINE_MODES.MAIN : TIMELINE_MODES.REFRAME;
        }

        if (switchTo === TIMELINE_MODES.REFRAME) {
            this.props.setLibraryPanel("", false);
            const selectedWorkspaceItem = targetItemId ? fromJS([targetItemId]) : this.props.selectedItems;
            const selectedItemId = selectedWorkspaceItem?.get(0);
            const selectedItem = selectedItemId
                ? project.getIn(["workspaceItems", selectedItemId])
                : null;

            if (selectedItem?.get("sceneData") || selectedItemId) {
                const parentId = selectedItem?.get("sceneData")
                    ? (selectedItem.get("originalId") || selectedItem.get("dropId"))
                    : selectedItemId;

                // Parent clip or scene clip selected — find the scene under the (potentially updated) playhead
                const match = this._findSceneAtPlayhead(
                    project.get("workspaceItems"),
                    actualPlayhead,
                    parentId
                );
                const hasMatchedScene = match?.itemIds?.length > 0;

                if (hasMatchedScene) {
                    this.pendingReframeSelection = {
                        selectedItems: match.dropIds,
                        selectedSubtitles: match.itemIds,
                    };
                }


                // If no scene found under playhead for this parent, just select the video clip
                this.props.setReframeMode(true);

                // Handle selection differently based on current mode
                if (isSubtitleMode) {
                    // In subtitle selection mode, preserve subtitle selections and update workspace selection
                    this.props.setSelectedItems({
                        selectedItems: [parentId]
                    });
                } else {
                    // In aireframe selection mode, select the scene immediately when available.
                    this.props.setSelectedItems({
                        selectedItems: hasMatchedScene ? [] : [parentId],
                        selectedSubtitles: hasMatchedScene ? [match.itemIds[0]] : []
                    });
                }
                return;
            }

            // Fallback to playhead if no explicit selection match found
            const match = this._findSceneAtPlayhead(
                project.get("workspaceItems"),
                actualPlayhead
            );

            if (match) {
                // Store selection in ref — applied in componentDidUpdate after mode change
                this.pendingReframeSelection = {
                    selectedItems: match.dropIds,
                    selectedSubtitles: (isSubtitleMode || switchTo === TIMELINE_MODES.REFRAME) ? match.itemIds : [],
                };
            }
            this.props.setReframeMode(true);
        } else {
            this.props.setReframeMode(false);
        }
    }

    addSubtitle() {
        const { project, } = this.props;
        const { timelineTile, } = this.state;

        const toUpdate = addSubtitleTimeline({
            project,
            timelineTile,
        });

        if (toUpdate) {
            this.props.updateTimelineTime({ toUpdate });
        }
    }

    addZoom() {
        const { project, playhead, updateTimelineTime, updateProjectData, setSelectedZoomId, setSelectedItems } = this.props;
        const selectedWorkspaceItem = this.props.selectedItems
        const selectedValue = this.props.selectedSubtitles
        const selectedItem = selectedValue.size > 0 ? getParentItemIdFromSubtitleId(project, selectedValue?.get(0)) : selectedWorkspaceItem.get(0);

        const item = project.getIn(["workspaceItems", selectedItem]);
        if (!item || playhead < item.get("enterStart")) {
            return;
        }

        applyZoomToTimeline({
            item,
            playhead,
            project,
            updateTimelineTime,
            updateProjectData,
            setSelectedZoomId,
            setSelectedItems,
            setIsSpaceUnavailable: (isUnavailable) => {
                if (isUnavailable) {
                    this.props.showFocusSpaceErrorModal({ show: true });
                }
            },
        });
    }

    zoomClear() {
        const { project } = this.props;
        const selectedWorkspaceItem = this.props.selectedItems
        const selectedValue = this.props.selectedSubtitles
        const selectedItem = selectedValue.size > 0 ? getParentItemIdFromSubtitleId(project, selectedValue?.get(0)) : selectedWorkspaceItem.get(0);
        const item = project.getIn(["workspaceItems", selectedItem]);
        if (!item) {
            return;
        }

        const toUpdate = [
            {
                id: item.get("id"),
                container: "zoomData",
                isDelete: true,
                itemId: item.get("id"),
            },
        ];

        if (toUpdate) {
            this.props.updateTimelineTime({ toUpdate });
            this.props.setPropertyWindow({
                component: "",
                type: "",
                isOpened: false,
            });
        }
    }

    render() {
        const { animatePlayhead, timelineTile, isSliderMultiSelect, selectedSliders, sliderMoved, animateTimelineContainer } = this.state;
        const { timelinePlot, sliders, stepper, tracks, matchingSnapPoints } = timelineTile;
        const {
            project,
            projectTimeLimit,
            isPlayAll,
            isPlayerLoaded,
            isWorkspaceDragging,
            playerShortcutName,
            shortcutName,
            timelineMode,
            isEnableVersionHistory,
            isMobileView
        } = this.props;
        const { timelineResizeStatus, slidersDragStatus, timelineScrollStatus } = this.props;

        let isDragging = false;
        if (
            isPlayAll
            || this.props.swapDetails
            || this.props.isCropping
            || isWorkspaceDragging
            || slidersDragStatus.isDragging
            || timelineResizeStatus.isDragging
            || timelineScrollStatus.isScrolling
            || isSliderMultiSelect
        ) {
            isDragging = true;
        }

        let allowPlayheadClick = !isDragging;
        if (isPlayerLoaded) {
            allowPlayheadClick = true;
        }

        let allowTimelineMouseMove = !isDragging;
        if (isPlayerLoaded) {
            allowTimelineMouseMove = true;
        }

        /** @todo remove eslint disable comment after canUseMagnet & canDeleteGap are implemented */
        /* eslint-disable-next-line */
        const { canUseMagnet, canDeleteGap } = this.checkGapStatus.executor([selectedSliders, sliders], { selectedSliders, sliders });

        let timelineCls = "timeline-tool-bar";
        if (isWorkspaceDragging) {
            timelineCls = `${timelineCls} tc--block-events`;
        }
        if (animateTimelineContainer) {
            timelineCls = `${timelineCls} tc--animate-container`;
        }


        return (
            <TimelineScrollProvider>
                <WaveformManager>
                    <TimelineContainer
                        className={timelineCls}
                        onTransitionEnd={this.onContainerTransitionEnd}
                        $timelinePlot={timelinePlot}
                    >
                        {!isPlayAll && !isEnableVersionHistory && (
                            <TimelineResize
                                className={timelineResizeStatus.isDragging ? "tr--is-dragging" : ""}
                                onPointerDown={!isDragging ? this.handleResizeMouseDown : undefined}
                            />
                        )}
                        <PlayerControls
                            timelineMode={timelineMode}
                            toggleSubtitleMode={this.toggleSubtitleMode}
                            timeScale={stepper.timeScale}
                            stepperInterval={stepper.interval}
                            onTimeScaleChange={!isDragging ? this.changeTimeScale : undefined}
                            timelineHeight={timelinePlot.height}
                            stopTimelineResize={this.stopTimelineResize}
                            adjustPlayheadStepBy={this.adjustPlayheadStepBy}
                            onSeekClick={this.onSeekClick}
                            playheadSlider={sliders[playheadId]}
                            rulerSlider={sliders[rulerId]}
                            shortcutName={playerShortcutName}
                            selectedSliders={selectedSliders}
                            isShowPlayControls={this.state.isShowPlayControls}
                            splitHandler={this.splitHandler}
                            addSubtitle={this.addSubtitle}
                            addZoom={this.addZoom}
                            zoomClear={this.zoomClear}
                            onDropdownClick={this.handleDropdownClick}
                            setOuterLayerVisible={this.props.setOuterLayerVisible}
                        />
                        <TimelineInner
                            ref={this.timelineInnerRef}
                            height={`calc(100% - ${PLAYER_CONTROLS_HEIGHT}px)`}
                            onPointerMove={allowTimelineMouseMove ? this.handleTimelineMouseMove : undefined}
                            onPointerLeave={allowTimelineMouseMove ? this.handleTimelineMouseLeave : undefined}
                        >
                            <Ruler
                                stepper={stepper}
                                sliders={sliders}
                                selectedSliders={selectedSliders}
                                timelinePlotWidth={timelinePlot.width}
                                projectTimeLimit={projectTimeLimit}
                                placePlayhead={allowPlayheadClick ? this.placePlayheadOnClick : undefined}
                                rulerScrollBy={this.rulerScrollBy}
                            />
                            <TrackList
                                trackListContainerRef={this.trackListContainerRef}
                                timelineInnerRef={this.timelineInnerRef}
                                tracks={tracks}
                                stepper={stepper}
                                sliders={sliders}
                                selectedSliders={selectedSliders}
                                timelinePlot={timelinePlot}
                                selectSlider={this.selectSlider}
                                isDragging={isDragging}
                                sliderMoved={sliderMoved}
                                isSliderDragging={slidersDragStatus.isDragging}
                                updateIsSliderMultiSelect={this.updateIsSliderMultiSelect}
                                matchingSnapPoints={matchingSnapPoints}
                                handleGapRemove={this.handleGapRemove}
                                canUseMagnet={canUseMagnet}
                                projectDetails={project}
                                shortcutName={shortcutName}
                                setSelectedSliders={this.setSelectedSliders}
                            />
                            {(sliders[indicatorId].isVisible && !isMobileView) && (
                                <Playhead
                                    stepperInterval={stepper.interval}
                                    isDragging={isDragging}
                                    thumb={sliders[indicatorId].enterStart}
                                />
                            )}
                            <Playhead
                                stepperInterval={stepper.interval}
                                isDragging={isDragging}
                                thumb={sliders[playheadId].enterStart}
                                startPlayheadThumbDrag={!isDragging ? this.selectSlider : undefined}
                                animatePosition={animatePlayhead}
                                onTransitionEnd={() => this.setState({ animatePlayhead: false })}
                                matchingSnapPoints={matchingSnapPoints}
                                sliders={sliders}
                                selectedSliders={selectedSliders}
                                splitHandler={this.splitHandler}
                            />
                        </TimelineInner>
                    </TimelineContainer>
                </WaveformManager>
            </TimelineScrollProvider >
        );
    }
}

TimelineComponent.propTypes = {
    timelineMode: PropTypes.number,
    interval: PropTypes.number,
    setLibraryPanel: PropTypes.func,
    setPropertyWindow: PropTypes.func,
    project: PropTypes.object,
    timelineResetToken: PropTypes.string,
    playheadResetToken: PropTypes.string,
    timelineSelectionAutoScrollToken: PropTypes.string,
    playhead: PropTypes.number,
    sideBarMenuWidth: PropTypes.number,
    isWorkspaceDragging: PropTypes.bool,
    isPlayAll: PropTypes.bool,
    t1: PropTypes.object,
    isPlayerLoaded: PropTypes.bool,
    selectedItems: PropTypes.object,
    selectedAudios: PropTypes.object,
    selectedSubtitles: PropTypes.object,
    projectTimeLimit: PropTypes.number,
    setTimelineHeight: PropTypes.func,
    setPlayHeadTime: PropTypes.func,
    updateTimelineTime: PropTypes.func,
    setSelectedItems: PropTypes.func,
    initiateTimelineResize: PropTypes.func,
    timelineResizeStatus: PropTypes.object,
    initiateSlidersDrag: PropTypes.func,
    slidersDragStatus: PropTypes.object,
    startTimelineScroll: PropTypes.func,
    stopTimelineScroll: PropTypes.func,
    timelineScrollStatus: PropTypes.object,
    shortcutName: PropTypes.string,
    playerShortcutName: PropTypes.string,
    swapDetails: PropTypes.object,
    isEnableVersionHistory: PropTypes.bool,
    setOuterLayerVisible: PropTypes.func,
    isCropping: PropTypes.bool,
    workspaceWidth: PropTypes.number,
    zoomFactor: PropTypes.number,
    isMobileView: PropTypes.bool,
    setTimelineCurrentState: PropTypes.func,
    cleanAudio: PropTypes.object,
    showUpgrades: PropTypes.func,
    updateProjectData: PropTypes.func,
    showFocusSpaceErrorModal: PropTypes.func,
    setSelectedZoomId: PropTypes.func,
    setReframeMode: PropTypes.func,
    appReframeData: PropTypes.object,
}

const mapStateToProps = (state) => {
    const propertyPanel = state.app.get("propertyPanel");
    const propertyWindow = state.app.get("propertyWindow");

    const sideBarMenuWidth = (
        state.app.getIn(["workspaceStage", "left", "menu"])
        + state.app.getIn(["workspaceStage", "left", "menuBorder"])
    );
    const isWorkspaceDragging = (
        state.app.get("isWorkspaceDragging")
        && state.app.getIn(["transformStatus", "transforming"])
    );

    let { projectTimeLimit } = state.userDetails;
    if (!Number.isFinite(projectTimeLimit)) {
        projectTimeLimit = -1;
    }

    const localSubtitle = state.projectDetails.get("localSubtitle");
    const localZoom = state.projectDetails.get("localZoom");
    const localAiReframe = state.projectDetails.get("localAiReframe");

    let timelineMode = TIMELINE_MODES.MAIN;

    // REFRAME mode takes priority (most specific mode)
    if (localAiReframe?.size && state?.app?.get("isReframeMode")) {
        timelineMode = TIMELINE_MODES.REFRAME;
    }
    // SMART_FOCUS mode is next priority
    else if (localZoom.size && (propertyWindow.get("component") === content.SMART_FOCUS || propertyWindow.get("component") === content.SMART_FOCUS_MOBILE)) {
        timelineMode = TIMELINE_MODES.SMART_FOCUS;
    }
    // SUBTITLE mode is least priority (most common)
    else if (localSubtitle.size && propertyPanel.get("selectedPanel") === PANEL.SUBTITLE) {
        timelineMode = TIMELINE_MODES.SUBTITLE;
    }


    return {
        timelineMode,
        interval: state.app.get("timelineInterval"),
        project: state.projectDetails,
        timelineResetToken: state.app.get("timelineResetToken"),
        playheadResetToken: state.app.get("playheadResetToken"),
        timelineSelectionAutoScrollToken: state.app.get("timelineSelectionAutoScrollToken"),
        playhead: state.app.get("playhead"),
        sideBarMenuWidth,
        isWorkspaceDragging,
        isPlayAll: state.app.get("isPlayAll"),
        t1: state.app.get("t1"),
        isPlayerLoaded: state.app.get("isLoaded"),
        selectedItems: state.app.get("selectedItems"),
        selectedAudios: state.app.get("selectedAudios"),
        selectedSubtitles: state.app.get("selectedSubtitles"),
        swapDetails: state.app.get("swapDetails"),
        projectTimeLimit,
        isEnableVersionHistory: state.app.get("isEnableVersionHistory"),
        isCropping: state.app.get("isCropping"),
        workspaceWidth: state.app.get("projectWidth"),
        zoomFactor: state.app.get("zoomFactor"),
        isMobileView: state.app.get("isMobileView"),
        cleanAudio: state.app.get("cleanAudio"),
        userDetails: state.userDetails,
        appReframeData: state.app.get("reframeData"),
    };
};

const mapDispatchToProps = (dispatch) => ({
    setLibraryPanel: (data, isExpand, context) => dispatch(setPropertyPanel(data, isExpand, context)),
    setTimelineHeight: (data) => dispatch(setTimelineHeight(data)),
    setPlayHeadTime: (data) => dispatch(setPlayHeadTime(data)),
    updateTimelineTime: (data) => dispatch(updateTimelineTime(data)),
    addObject: (data) => dispatch(addObject(data)),
    setSelectedItems: (data) => dispatch(setSelectedItems(data)),
    setTimelineCurrentState: (data) => dispatch(setCurrentState(data)),
    showUpgrades: (data) => dispatch(showUpgrade(data)),
    setPropertyWindow: (data) => dispatch(setPropertyWindow(data)),
    updateProjectData: (data) => dispatch(updateProjectData(data)),
    showFocusSpaceErrorModal: (data) => dispatch(showFocusSpaceErrorModal(data)),
    setSelectedZoomId: (data) => dispatch(setSelectedZoomId(data)),
    setReframeMode: (data) => dispatch(setReframeMode(data)),

});

const Timeline = connect(mapStateToProps, mapDispatchToProps)(TimelineComponent);

const TimelineWithEvents = (props) => {
    return (
        <MouseDrag>
            {(timelineResizeProps) => (
                <MouseDrag>
                    {(sliderDragProps) => (
                        <AutoScroll>
                            {(timelineScrollProps) => (
                                <Timeline
                                    {...props}
                                    initiateTimelineResize={timelineResizeProps.initiateDrag}
                                    timelineResizeStatus={timelineResizeProps.dragStatus}
                                    initiateSlidersDrag={sliderDragProps.initiateDrag}
                                    slidersDragStatus={sliderDragProps.dragStatus}
                                    startTimelineScroll={timelineScrollProps.startScroll}
                                    stopTimelineScroll={timelineScrollProps.stopScroll}
                                    timelineScrollStatus={timelineScrollProps.scrollStatus}
                                />
                            )}
                        </AutoScroll>
                    )}
                </MouseDrag>
            )}
        </MouseDrag>
    )
}

export default TimelineWithEvents;
